import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ScenarioRunner.css';
import { getActiveScenario, getActiveScenarioId } from '../../scenarioRegistry';
import youtubeCues from '@seed/canonical/audio/youtube_cues.json';
// 2026-06-07 (UX transparency): skill formula seed lookup for the StatsPanel
// "show your work" affordance. Each skill row surfaces its primary-pair
// formula (e.g. `[(PER+DEX)/2] + Skill Level`) inline under the PC/Total
// numbers so the player sees how PC was derived.
import { substituteSkillFormula } from '../../engine/skillFormulaSubstitution';
import type { PCPregen } from '../../types/pregen';
import type { CampaignMeta } from '../../services/types';
import type {
  AdventureScenario,
  DrawerSection,
  EndingType,
  GenericRollEntry,
  RollLog,
  RunnerView,
  ScenarioDecisionEdge,
  ScenarioParagraph,
  ScenarioRunnerState,
  SkillCheckEdge,
} from '../../types/scenario';
import { SCENARIO_STATE_SCHEMA_VERSION } from '../../types/scenario';
import {
  EASE_FACTORS,
  computeSuccessChance,
  computeQualityRatingBrackets,
  resolveTask,
  type EaseFactor,
} from '../../engine/resolution';
import {
  BRIEFING_SCENE,
  PARAGRAPH_SCENE_MAP,
  SCENE_ALT_TEXT,
  SCENE_IMAGE_URLS,
  lookupSceneImage,
} from './sceneMap';
import {
  YouTubeTheaterPlayer,
  type YouTubeCueMap,
} from '../../services/audio/YouTubeTheaterPlayer';
import type { CrossScenarioRecognition } from '../../services/host/FameRecognitionServiceModule';
import { ReadAloudControl } from '../atmosphere/ReadAloudControl';
import { VoicePickerSection } from '../atmosphere/VoicePickerSection';
import { MicControl } from '../atmosphere/MicControl';
import { MicActivationSection } from '../atmosphere/MicActivationSection';
import { TalkBar } from '../atmosphere/TalkBar';
import { DiceRollTumble } from '../atmosphere/DiceRollTumble';
import { VoiceDebugControl } from '../atmosphere/VoiceDebugControl';
import { DiceChipPortal } from '../atmosphere/DiceChipPortal';
import { QRCinematicResponse } from '../atmosphere/QRCinematicResponse';
import { HeroPointChipPortal } from '../atmosphere/HeroPointChipPortal';
import { AtmosphereSettingsPanel } from '../settings/AtmosphereSettingsPanel';
import { ExportDossierButton } from './ExportDossierButton';
import { BatchOverrideConfirm } from './BatchOverrideConfirm';
import { BritishInsignia } from './BritishInsignia';
import { ForceRecognizeToggle } from './ForceRecognizeToggle';
import { ManualRecognitionBadge } from './ManualRecognitionBadge';
import { ThemeDrawer } from './ThemeDrawer';
import type { UseThemeSelectionResult } from '../../hooks/useThemeSelection';
import { FameRecognitionCinematic } from '../atmosphere/FameRecognitionCinematic';
// V9.2.x.2 (2026-06-06) — 11 V9.1 dispatch-action surface cards.
// Each mounts conditionally when its useGMDispatcher callback fires.
import { ChaseSequenceCard, type ChaseSequenceCardProps } from './ChaseSequenceCard';
import { PersuasionOutcomeChip, type PersuasionOutcomeChipProps } from './PersuasionOutcomeChip';
import { SeductionStageTracker, type SeductionStageTrackerProps } from './SeductionStageTracker';
import { FameRecognitionPanel, type FameRecognitionPanelProps } from './FameRecognitionPanel';
import { HeroPointSpendView, type HeroPointSpendViewProps } from './HeroPointSpendView';
import { NPCEncounterCard, type NPCEncounterCardProps } from './NPCEncounterCard';
import { TravelTimeBubble, type TravelTimeBubbleProps } from './TravelTimeBubble';
import { XPAwardSummary, type XPAwardSummaryProps } from './XPAwardSummary';
import { GamblingTableView, type GamblingTableViewProps } from './GamblingTableView';
import { RepairAttemptIndicator, type RepairAttemptIndicatorProps } from './RepairAttemptIndicator';
import {
  CharacterCreationFlowStepper,
  type CharacterCreationFlowStepperProps,
} from './CharacterCreationFlowStepper';
import { describeFameRecognition } from '../../engine/narrative';
import type { DossierNpc } from '../../services/export/DossierExporter';
import { useHeroPointSpend } from '../../hooks/useHeroPointSpend';
import { QR_NAMES } from '../../engine/tables';
import { useRollCinematicStore } from '../../stores/rollCinematicStore';
import { VoiceLogDrawer } from './VoiceLogDrawer';
import { useVoiceLog } from '../../hooks/useVoiceLog';
import { useGMDispatcher } from '../../hooks/useGMDispatcher';
import { useTtsQueue } from '../../hooks/useTtsQueue';
import { useAutoNarrate } from '../../hooks/useAutoNarrate';
import { useVisitedParagraphs } from '../../hooks/useVisitedParagraphs';
import { TtsQueueProvider } from '../../contexts/TtsQueueContext';
import { NarrationModeSection } from '../atmosphere/NarrationModeSection';
import { UndoBanner } from './UndoBanner';
import { AckBanner } from './AckBanner';
import { NpcCard } from './NpcCard';
import { NpcDrawer } from './NpcDrawer';
// V7.2.B (2026-06-03) — PDF library drawer + viewer
import { PdfLibraryDrawer } from './PdfLibraryDrawer';
import { ModulesLibraryDrawer } from './ModulesLibraryDrawer';
import { EquipmentLibraryDrawer } from './EquipmentLibraryDrawer';
import { VehiclesLibraryDrawer } from './VehiclesLibraryDrawer';
import { GadgetsLibraryDrawer } from './GadgetsLibraryDrawer';
import { DrugsLibraryDrawer } from './DrugsLibraryDrawer';
import { QBranchPersonnelDrawer } from './QBranchPersonnelDrawer';
import { NpcArchetypesDrawer } from './NpcArchetypesDrawer';
import { CanonicalNpcsDrawer } from './CanonicalNpcsDrawer';
import { VillainsDossierDrawer } from './VillainsDossierDrawer';
import { ThrillingLocationsNpcsDrawer } from './ThrillingLocationsNpcsDrawer';
import { PdfViewerModal } from './PdfViewerModal';
import { usePdfLibrary } from '../../hooks/usePdfLibrary';
import { useModulesLibrary } from '../../hooks/useModulesLibrary';
import { useEquipmentLibrary } from '../../hooks/useEquipmentLibrary';
import { useVehiclesLibrary } from '../../hooks/useVehiclesLibrary';
import { useGadgetsLibrary } from '../../hooks/useGadgetsLibrary';
import { useDrugsLibrary } from '../../hooks/useDrugsLibrary';
import { useQBranchPersonnelLibrary } from '../../hooks/useQBranchPersonnelLibrary';
import { useNpcArchetypesLibrary } from '../../hooks/useNpcArchetypesLibrary';
import { useCanonicalNpcsLibrary } from '../../hooks/useCanonicalNpcsLibrary';
import { useVillainsLibrary } from '../../hooks/useVillainsLibrary';
import { useThrillingLocationsLibrary } from '../../hooks/useThrillingLocationsLibrary';
import { QuickRollButton } from './QuickRollButton';
import { SettingsToggles } from './SettingsToggles';
import { HelpIcon } from '../help/HelpIcon';
import { usePlayerSettings } from '../../services/playerSettings';
import { useNpcLookup } from '../../hooks/useNpcLookup';
import { useNpcEncounters } from '../../hooks/useNpcEncounters';
import { useFameRecognition } from '../../hooks/useFameRecognition';
import {
  detectRollTrigger,
  resolveSkillToPregenKey,
  type RollTrigger,
  paragraphMentionsRoll,
} from '../../services/rollTriggers';
import type { NpcSheetData } from '../../types/scenario';
import type {
  DispatchView,
  DispatchHistoryTurn,
} from '../../services/types';
import {
  AMBIENT_FADE_MS,
  BRIEFING_CUE_ID,
  ENDING_TO_STING_CUE_ID,
  LOCATION_TO_CUE_ID,
  MUTE_FADE_MS,
  STING_CROSSFADE_MS,
  UNMOUNT_FADE_MS,
  readMutePreference,
  readVolumePreference,
  writeMutePreference,
  writeVolumePreference,
} from './audioCueMap';

// C5 (2026-06-07): scenario is now selected at runtime via
// scenarioRegistry. Default = dr_no_adventure; the Module Library
// drawer's Play button writes a new id to localStorage + forces
// reload — this const re-initializes on next mount.
const scenario = getActiveScenario();
const activeScenarioId = getActiveScenarioId();

const PARAGRAPH_MIN = 1;
const PARAGRAPH_MAX = 102;
const GO_TO_REGEX = /Go to ¶(\d+)\./;

/**
 * The lookup table for ending-type display labels. Mapped 1-1 with EndingType
 * union per V9 §7.4 + M3b.6 §4.7 user-approved replace-view + badge design.
 */
const ENDING_LABELS: Record<EndingType, string> = {
  victory: 'Mission Accomplished',
  rescue_victory_sub_route: 'Rescued — Mission Accomplished',
  pyrrhic_victory_death: 'Pyrrhic Victory',
  death: 'Killed in Action',
  catastrophic_failure: 'Catastrophic Failure',
};

// ─── Pure helpers (exported for unit tests) ──────────────────────────────

/**
 * Parse a "Go to ¶X." prose terminator out of a paragraph's text. Returns the
 * paragraph number or null if no match (or the number is out of valid 1-102
 * range, in which case the runner shows a data-integrity error).
 *
 * Per M3b.6 §4.8 user-approved default: pure-narrative paragraphs (¶55,
 * ¶68, ¶70, ¶90, ¶99, …) without `decisions[]` or `leads_to_paragraph_immediate`
 * end with the literal "Go to ¶N." — the runner adds a synthetic Continue
 * button extracted via this regex.
 */
export function parseGoToParagraph(text: string): number | null {
  const m = text.match(GO_TO_REGEX);
  if (!m || !m[1]) return null;
  const n = parseInt(m[1], 10);
  if (n < PARAGRAPH_MIN || n > PARAGRAPH_MAX) return null;
  return n;
}

/**
 * Scan a decision-edge object for any inline `note_..._offrange` annotation.
 * Source contains keys like `note_paragraph_108_offrange`, `note_offrange`,
 * `note_paragraph_107_offrange`, etc. (annotated when a `leads_to_paragraph`
 * points outside the published 1-102 range).
 *
 * Per M3b.6 §4.5: such decisions render disabled with a warning icon; this
 * helper retrieves the source-provided explanation for the warning title.
 */
export function detectOffRangeAnnotation(
  decision: ScenarioDecisionEdge,
): string | null {
  const obj = decision as unknown as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (
      (key.startsWith('note_paragraph_') && key.endsWith('_offrange')) ||
      key === 'note_offrange'
    ) {
      const value = obj[key];
      if (typeof value === 'string') return value;
    }
  }
  return null;
}

/**
 * Test whether a decision-edge points to a valid in-range paragraph.
 * Returns false for null (outcome-describing) AND for off-range numbers.
 * Returns false for skill_check edges (routing is determined by roll outcome,
 * not by a static leads_to_paragraph).
 */
export function isInRangeDecision(decision: ScenarioDecisionEdge): boolean {
  if (decision.kind === 'skill_check') return false;
  if (decision.leads_to_paragraph === null) return false;
  return (
    decision.leads_to_paragraph >= PARAGRAPH_MIN &&
    decision.leads_to_paragraph <= PARAGRAPH_MAX
  );
}

/**
 * Look up a story paragraph by number. Returns undefined if not present
 * (would only happen for off-range numbers — runner guards against this via
 * button-disable in the UI layer).
 */
export function findParagraph(n: number): ScenarioParagraph | undefined {
  return scenario.story_paragraphs.find((p) => p.number === n);
}

// ─── LocalStorage persistence ────────────────────────────────────────────

/**
 * Build the localStorage key for a (campaign × pregen) tuple. Per M3b.6
 * §4.6 user-approved default: persistence is local-only (no backend write).
 * Two different PCs in the same campaign get independent state.
 */
export function makeStateKey(campaignId: string, pregenId: string): string {
  return `briefing-room:scenario-state:${campaignId}:${pregenId}`;
}

export function readScenarioState(
  key: string,
): ScenarioRunnerState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScenarioRunnerState>;
    // Minimal shape sanity check — drop the read if shape doesn't match.
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.view ||
      !Array.isArray(parsed.history) ||
      !Array.isArray(parsed.rolls)
    ) {
      return null;
    }
    // Schema-version gate: accept SCENARIO_STATE_SCHEMA_VERSION OR a
    // legacy blob whose schemaVersion field is absent (pre-2026-06-08
    // writes — structurally compatible with v1 because every new field
    // since the original shape has been optional). Any other concrete
    // value indicates a forward-incompatible save (e.g., a future v2
    // shape this build doesn't understand) and is rejected so the
    // consumer falls through to DEFAULT_STATE.
    if (
      parsed.schemaVersion !== undefined &&
      parsed.schemaVersion !== SCENARIO_STATE_SCHEMA_VERSION
    ) {
      return null;
    }
    return { ...parsed, schemaVersion: SCENARIO_STATE_SCHEMA_VERSION } as ScenarioRunnerState;
  } catch {
    // Quota / privacy mode / malformed JSON — degrade to default.
    return null;
  }
}

export function writeScenarioState(
  key: string,
  state: ScenarioRunnerState,
): void {
  if (typeof window === 'undefined') return;
  try {
    // Stamp the current canonical version on every write so legacy blobs
    // are silently upgraded on the next state change and a future reader
    // can detect forward-incompatible writes via schemaVersion mismatch.
    const stamped: ScenarioRunnerState = {
      ...state,
      schemaVersion: SCENARIO_STATE_SCHEMA_VERSION,
    };
    window.localStorage.setItem(key, JSON.stringify(stamped));
  } catch {
    // Same App.tsx markOpenerPlayed degradation pattern.
  }
}

export function clearScenarioState(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Same degradation pattern.
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────

interface BriefingViewProps {
  onChoose: (n: number) => void;
  onTranscript?: (text: string) => void;
  /** V7.1: inline NPC sheets rendered between the briefing text and the
   * decision buttons (book-faithful layout). */
  inlineNpcs?: NpcSheetData[];
}

function BriefingView({ onChoose, onTranscript, inlineNpcs }: BriefingViewProps) {
  const cb = scenario.character_briefing;
  // C5 (2026-06-07): per-paragraph + briefing scene images are dr_no-only
  // for the V9→V7 first ship. Other scenarios render text-only briefings
  // until per-scenario scene maps are authored.
  const showHero = activeScenarioId === 'dr_no_adventure';
  return (
    <article className="br-scenario-briefing" data-testid="br-scenario-briefing">
      {showHero && (
        <figure
          className="br-scenario-hero"
          data-testid="br-scenario-hero-briefing"
          data-scene-location={BRIEFING_SCENE}
        >
          <img
            className="br-scenario-hero-img"
            src={SCENE_IMAGE_URLS[BRIEFING_SCENE]}
            alt={SCENE_ALT_TEXT[BRIEFING_SCENE]}
          />
        </figure>
      )}
      <header className="br-scenario-paragraph-header">
        <h1 className="br-display">{cb.title}</h1>
        <p className="br-label">Page {cb.page} · Pre-mission Briefing</p>
        <ReadAloudControl
          text={cb.briefing_text_verbatim}
          testidPrefix="br-read-aloud-briefing"
        />
        <MicControl testidPrefix="br-mic-briefing" onTranscript={onTranscript} />
      </header>
      <p className="br-scenario-text br-body">{cb.briefing_text_verbatim}</p>
      {inlineNpcs !== undefined && inlineNpcs.length > 0 && (
        <div
          className="br-scenario-inline-npcs"
          data-testid="br-scenario-briefing-inline-npcs"
        >
          {inlineNpcs.map((n) => (
            <NpcCard
              key={n.id}
              npc={n}
              testidPrefix={`br-scenario-briefing-npc-${n.id}`}
            />
          ))}
        </div>
      )}
      <ul className="br-scenario-decisions">
        {cb.first_decision_paragraphs.map((d, i) => {
          const choiceLetter = String.fromCharCode(97 + i);
          // Briefing edges are always NarrativeEdge (no skill checks on the
          // pre-mission briefing page). Skill_check edges here are guarded out.
          if (d.kind === 'skill_check') return null;
          const target = d.leads_to_paragraph;
          if (target === null) return null;
          return (
            <li key={i}>
              <button
                type="button"
                className="br-scenario-decision-button"
                data-testid={`br-scenario-briefing-choice-${choiceLetter}`}
                onClick={() => onChoose(target)}
              >
                {d.label}
              </button>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

interface DecisionListProps {
  paragraphNumber: number;
  decisions: ScenarioDecisionEdge[];
  onChoose: (n: number) => void;
  testidPrefix: string;
  /** Workstream A 2026-06-08: fires when a skill_check edge is clicked so
   *  the engine can run executeRoll and arm pendingSkillCheckEdge for
   *  post-QR-cinematic routing. */
  onSkillCheckEdge?: (edge: SkillCheckEdge) => void;
  /** 2026-06-09 Bug 1 fix: when provided, skill_check edges whose
   *  `skillName` does not resolve against this set via
   *  `resolveSkillToPregenKey` render disabled with a tooltip explaining
   *  the mismatch — mirrors QuickRollButton.tsx:65-69. Pre-fix the button
   *  was enabled but `executeRoll` silently returned false
   *  (ScenarioRunner.tsx:2097) leaving the player stuck on the paragraph.
   *  Omitted → no availability check (legacy + unit-test callers retain
   *  prior behavior). */
  pregenSkillKeys?: string[];
}

function DecisionList({
  paragraphNumber,
  decisions,
  onChoose,
  testidPrefix,
  onSkillCheckEdge,
  pregenSkillKeys,
}: DecisionListProps) {
  return (
    <ul className="br-scenario-decisions">
      {decisions.map((d, i) => {
        // Workstream A 2026-06-08: discriminate on kind for routing.
        if (d.kind === 'skill_check') {
          // 2026-06-09 Bug 1 fix: silently-no-op skill_check edges when the
          // character lacks the required skill. resolveSkillToPregenKey
          // returns null when neither the direct name nor any SKILL_ALIASES
          // entry maps to a pregen skill key. Matches the same resolution
          // step `executeRoll` uses internally so a disabled button here
          // accurately predicts an unrollable edge.
          const skillResolvable =
            pregenSkillKeys === undefined ||
            resolveSkillToPregenKey(d.skillName, pregenSkillKeys) !== null;
          const skillCheckTitle = skillResolvable
            ? `Roll ${d.skillName} EF ${d.easeFactor} — outcome determines destination`
            : `Your character does not have the ${d.skillName} skill — pick an alternative decision, or return to the Briefing Room to switch characters.`;
          return (
            <li key={i}>
              <button
                type="button"
                className="br-scenario-decision-button br-scenario-decision-button--skill-check"
                data-testid={`${testidPrefix}-${paragraphNumber}-${i}`}
                data-edge-kind="skill_check"
                data-no-skill={!skillResolvable ? 'true' : undefined}
                disabled={!skillResolvable}
                onClick={() => onSkillCheckEdge?.(d)}
                title={skillCheckTitle}
              >
                <span className="br-scenario-decision-label">{d.label}</span>
                <span
                  className="br-scenario-decision-roll-badge"
                  aria-hidden="true"
                  data-testid={`${testidPrefix}-${paragraphNumber}-${i}-roll-badge`}
                >
                  🎲
                </span>
                {!skillResolvable && (
                  <span
                    className="br-scenario-decision-warning"
                    aria-hidden="true"
                    data-testid={`${testidPrefix}-${paragraphNumber}-${i}-no-skill-icon`}
                  >
                    ⚠
                  </span>
                )}
              </button>
              {d.note && <p className="br-label br-scenario-decision-note">{d.note}</p>}
            </li>
          );
        }
        // NarrativeEdge (default kind) — existing rendering unchanged.
        const inRange = isInRangeDecision(d);
        const isOutcomeOnly = d.leads_to_paragraph === null;
        const offRangeNote = detectOffRangeAnnotation(d);
        const ariaTitle = isOutcomeOnly
          ? 'Roll outcome — not a navigation choice'
          : !inRange
            ? `Source typo: ¶${d.leads_to_paragraph} not in published 1-102 range${offRangeNote ? ` — ${offRangeNote}` : ''}`
            : undefined;
        return (
          <li key={i}>
            <button
              type="button"
              className="br-scenario-decision-button"
              data-testid={`${testidPrefix}-${paragraphNumber}-${i}`}
              data-off-range={!inRange && !isOutcomeOnly ? 'true' : undefined}
              data-outcome={isOutcomeOnly ? 'true' : undefined}
              disabled={!inRange}
              onClick={() => {
                if (inRange && d.leads_to_paragraph !== null) {
                  onChoose(d.leads_to_paragraph);
                }
              }}
              title={ariaTitle}
            >
              <span className="br-scenario-decision-label">{d.label}</span>
              {!inRange && !isOutcomeOnly && (
                <span
                  className="br-scenario-decision-warning"
                  aria-hidden="true"
                  data-testid={`${testidPrefix}-${paragraphNumber}-${i}-offrange-icon`}
                >
                  ⚠
                </span>
              )}
              {isOutcomeOnly && (
                <span
                  className="br-scenario-decision-warning"
                  aria-hidden="true"
                  data-testid={`${testidPrefix}-${paragraphNumber}-${i}-outcome-icon`}
                >
                  ○
                </span>
              )}
            </button>
            {d.note && <p className="br-label br-scenario-decision-note">{d.note}</p>}
          </li>
        );
      })}
    </ul>
  );
}

interface ParagraphViewProps {
  paragraph: ScenarioParagraph;
  onChoose: (n: number) => void;
  onTranscript?: (text: string) => void;
  /** V7.1: inline NPC sheets between paragraph text and decisions. */
  inlineNpcs?: NpcSheetData[];
  /** V7.2.A: detected roll trigger to render an inline Quick-roll button. */
  rollTrigger?: RollTrigger | null;
  /** V7.2.A: true when the PC has the detected skill. */
  rollSkillAvailable?: boolean;
  /** V7.2.A: caller runs the actual roll on click. */
  onQuickRoll?: (skill: string, ef: string) => void;
  /** 2026-06-07: opens the Roll Dice drawer so the player can pick a
   *  skill + EF manually. Rendered as a "Pick roll…" button next to the
   *  QuickRollButton when the paragraph mentions a roll. */
  onPickRoll?: () => void;
  /** Workstream A 2026-06-08: fires when a skill_check decision edge is
   *  clicked. The engine runs executeRoll + stores the edge so
   *  handleQRComplete can route to the correct outcome paragraph. */
  onSkillCheckEdge?: (edge: SkillCheckEdge) => void;
  /** 2026-06-09 Bug 1 fix: threaded to DecisionList so skill_check edges
   *  whose skillName doesn't resolve via SKILL_ALIASES render disabled
   *  with a tooltip. See DecisionListProps.pregenSkillKeys docstring. */
  pregenSkillKeys?: string[];
}

function ParagraphView({
  paragraph,
  onChoose,
  onTranscript,
  inlineNpcs,
  rollTrigger,
  rollSkillAvailable,
  onQuickRoll,
  onPickRoll,
  onSkillCheckEdge,
  pregenSkillKeys,
}: ParagraphViewProps) {
  const { autoRollEnabled } = usePlayerSettings();
  // 2026-06-07: paragraph-level "mentions roll" detector. Triggers the
  // "Pick roll" + "Ask GM" assist buttons even when no specific skill is
  // named in the prose. False when the paragraph isn't about a roll.
  // 2026-06-08 Fix #B — also scan decision labels. VG35014 ¶6 puts the
  // "Perception roll (Base Time 5 min)" requirement in decision A's label
  // not in the body prose; pre-fix the assist row never appeared. AVTAK
  // ¶33 (Mountaineering / STR), ¶38 (Demolitions QR3+) and ¶46
  // (Mountaineering -1 EF) had the same gap. Combining body + labels
  // before running the detector closes all of them at once.
  const detectionText = useMemo(
    () =>
      [paragraph.text, ...(paragraph.decisions ?? []).map((d) => d.label)].join(
        ' ',
      ),
    [paragraph.text, paragraph.decisions],
  );
  const mentionsRoll = useMemo(
    () => paragraphMentionsRoll(detectionText),
    [detectionText],
  );
  // Voice-prompt template fired when the player clicks "Ask GM". The
  // existing voice dispatcher consumes the transcript + decides the
  // skill + EF based on the active paragraph context.
  const askGmTemplate = `What roll should I make here on paragraph ${paragraph.number}?`;
  const synthetic = useMemo(
    () =>
      !paragraph.decisions &&
      paragraph.leads_to_paragraph_immediate === undefined
        ? parseGoToParagraph(paragraph.text)
        : null,
    [paragraph],
  );

  const scene = useMemo(
    () => lookupSceneImage(paragraph.number),
    [paragraph.number],
  );

  return (
    <article
      className="br-scenario-paragraph"
      data-testid={`br-scenario-paragraph-${paragraph.number}`}
    >
      {scene && (
        <figure
          className="br-scenario-hero"
          data-testid={`br-scenario-hero-${paragraph.number}`}
          data-scene-location={scene.locationId}
        >
          <img
            className="br-scenario-hero-img"
            src={scene.url}
            alt={scene.alt}
          />
        </figure>
      )}
      <header className="br-scenario-paragraph-header">
        <p className="br-label">
          ¶{paragraph.number} · Page {paragraph.page}
        </p>
        <ReadAloudControl
          text={paragraph.text}
          testidPrefix={`br-read-aloud-paragraph-${paragraph.number}`}
        />
        <MicControl
          testidPrefix={`br-mic-paragraph-${paragraph.number}`}
          onTranscript={onTranscript}
        />
      </header>
      <p className="br-scenario-text br-body">{paragraph.text}</p>
      {rollTrigger !== null && rollTrigger !== undefined && onQuickRoll && (
        <span className="br-quick-roll-with-help">
          <QuickRollButton
            trigger={rollTrigger}
            available={rollSkillAvailable ?? false}
            onRoll={onQuickRoll}
            autoFire={autoRollEnabled}
            testidPrefix={`br-quick-roll-paragraph-${paragraph.number}`}
          />
          <HelpIcon id="quick-roll-button" side="below" />
        </span>
      )}
      {/* 2026-06-07: roll-assist row. When the paragraph mentions a roll —
       *  whether or not a specific skill was detected — surface a "Pick
       *  roll" (opens the Roll drawer) + "Ask GM" (fires a voice-prompt
       *  through the dispatcher) so the player isn't left guessing the
       *  skill or EF. The buttons compose with the QuickRollButton above
       *  when both apply. */}
      {mentionsRoll && (
        <div
          className="br-scenario-roll-assist"
          data-testid={`br-scenario-roll-assist-paragraph-${paragraph.number}`}
        >
          {onPickRoll !== undefined && (
            <>
              <button
                type="button"
                className="br-scenario-roll-assist-button br-label"
                data-testid={`br-scenario-roll-assist-pick-${paragraph.number}`}
                onClick={onPickRoll}
                title="Open the Roll drawer to pick a skill + Ease Factor manually."
              >
                🎯 Pick roll…
              </button>
              <HelpIcon id="pick-roll-button" side="below" />
            </>
          )}
          {onTranscript !== undefined && (
            <>
              <button
                type="button"
                className="br-scenario-roll-assist-button br-label"
                data-testid={`br-scenario-roll-assist-ask-gm-${paragraph.number}`}
                onClick={() => onTranscript(askGmTemplate)}
                title="Ask the GM by voice — the dispatcher picks the skill + EF for this paragraph."
              >
                🎤 Ask GM
              </button>
              <HelpIcon id="ask-gm-button" side="below" />
            </>
          )}
        </div>
      )}
      {inlineNpcs !== undefined && inlineNpcs.length > 0 && (
        <div
          className="br-scenario-inline-npcs"
          data-testid={`br-scenario-paragraph-${paragraph.number}-inline-npcs`}
        >
          {inlineNpcs.map((n) => (
            <NpcCard
              key={n.id}
              npc={n}
              testidPrefix={`br-scenario-paragraph-${paragraph.number}-npc-${n.id}`}
            />
          ))}
        </div>
      )}

      {paragraph.leads_to_paragraph_immediate !== undefined && (
        <button
          type="button"
          className="br-scenario-decision-button"
          data-testid={`br-scenario-continue-${paragraph.number}`}
          onClick={() => onChoose(paragraph.leads_to_paragraph_immediate!)}
        >
          Continue → ¶{paragraph.leads_to_paragraph_immediate}
        </button>
      )}

      {paragraph.decisions && paragraph.decisions.length > 0 && (
        <DecisionList
          paragraphNumber={paragraph.number}
          decisions={paragraph.decisions}
          onChoose={onChoose}
          testidPrefix="br-scenario-decision"
          onSkillCheckEdge={onSkillCheckEdge}
          pregenSkillKeys={pregenSkillKeys}
        />
      )}

      {paragraph.post_roll_narrative && (
        <section
          className="br-scenario-post-roll"
          data-testid={`br-scenario-post-roll-${paragraph.number}`}
        >
          <p className="br-scenario-text br-body">{paragraph.post_roll_narrative}</p>
          {paragraph.narrative_decisions && paragraph.narrative_decisions.length > 0 && (
            <DecisionList
              paragraphNumber={paragraph.number}
              decisions={paragraph.narrative_decisions}
              onChoose={onChoose}
              testidPrefix="br-scenario-narrative-decision"
              onSkillCheckEdge={onSkillCheckEdge}
              pregenSkillKeys={pregenSkillKeys}
            />
          )}
        </section>
      )}

      {synthetic !== null && (
        <button
          type="button"
          className="br-scenario-decision-button"
          data-testid={`br-scenario-continue-${paragraph.number}`}
          onClick={() => onChoose(synthetic)}
        >
          Continue → ¶{synthetic}
        </button>
      )}

      {paragraph.gm_notes && paragraph.gm_notes.length > 0 && (
        <details className="br-scenario-gm-notes" data-testid={`br-scenario-gm-notes-${paragraph.number}`}>
          <summary>GM Notes ({paragraph.gm_notes.length})</summary>
          <ul>
            {paragraph.gm_notes.map((n, i) => (
              <li key={i} className="br-body">
                {n}
              </li>
            ))}
          </ul>
        </details>
      )}
    </article>
  );
}

interface EndingViewProps {
  paragraphNumber: number;
  endingType: EndingType;
  history: number[];
  onPlayAgain: () => void;
}

function EndingView({
  paragraphNumber,
  endingType,
  history,
  onPlayAgain,
}: EndingViewProps) {
  const para = findParagraph(paragraphNumber);
  return (
    <article
      className="br-scenario-ending"
      data-testid="br-scenario-ending"
      data-ending-type={endingType}
    >
      <header className="br-scenario-ending-header">
        <span
          className="br-scenario-ending-badge"
          data-ending-type={endingType}
          data-testid="br-scenario-ending-badge"
        >
          {ENDING_LABELS[endingType]}
        </span>
        <p className="br-label">
          ¶{paragraphNumber} · Page {para?.page ?? '?'}
        </p>
      </header>
      <p className="br-scenario-text br-body">
        {para?.text ?? '(Source: paragraph text unavailable)'}
      </p>

      <section className="br-scenario-ending-path">
        <h2 className="br-display">Your Path</h2>
        <p className="br-label" data-testid="br-scenario-ending-path">
          {history.length > 0 ? history.join(' → ') : '(no decisions recorded)'}
        </p>
      </section>

      <button
        type="button"
        className="br-scenario-play-again"
        data-testid="br-scenario-play-again"
        onClick={onPlayAgain}
      >
        Play Again — Different Character
      </button>
    </article>
  );
}

interface StatsPanelProps {
  pregen: PCPregen;
}

// 2026-06-07 (audit pass): skill formula substitution lives in the engine
// layer at engine/skillFormulaSubstitution.ts so it's testable in isolation
// (vitest pins string output + verifies every pregen×skill row's PC matches
// the computed value — regression guard against seed-data drift).

function StatsPanel({ pregen }: StatsPanelProps) {
  const stats = pregen.stats;
  // Seduction's formula form is `[(WIL + Charisma SL)/2] + Skill Level`;
  // null when this PC doesn't have Charisma (so Seduction substitution still
  // renders, with the Charisma SL treated as 0 — mirrors resolution.ts).
  const charismaSkillLevel = pregen.skills['Charisma']?.skill_level ?? null;
  const skillEntries = Object.entries(pregen.skills).sort(
    (a, b) => b[1].primary_chance - a[1].primary_chance,
  );
  return (
    <aside
      className="br-scenario-drawer"
      data-testid="br-scenario-drawer-stats"
      aria-label="Character stats"
    >
      <header>
        <h2 className="br-display">{pregen.name}</h2>
        <p className="br-label">{pregen.code_name} · Rank {pregen.rank}</p>
      </header>
      {/* 2026-06-07: glossary block so the player can decode the numbers
       *  in the rest of the panel. Per feedback_stat_calc_details_hidden. */}
      <section
        className="br-scenario-drawer-section"
        data-testid="br-scenario-stats-glossary"
      >
        <h3 className="br-label">What the numbers mean</h3>
        <ul className="br-scenario-stats-glossary">
          <li className="br-label">
            <strong>PC</strong> · Primary Chance — the d100 you must roll under
            (player rolls). Lower 1d100 ≤ PC × EF = success.
          </li>
          <li className="br-label">
            <strong>SL</strong> · Skill Level 1–15 — training rank in the skill.
            Plugged into the formula to derive PC (e.g. PER+DEX averaged + SL).
          </li>
          <li className="br-label">
            <strong>EF</strong> · Ease Factor 1–10 — task difficulty multiplier
            picked at the GM's call. Higher = easier (more room to succeed).
          </li>
          <li className="br-label">
            <strong>SC</strong> · Success Chance = PC × EF. Roll ≤ SC to pass.
          </li>
          <li className="br-label">
            <strong>QR</strong> · Quality Rating 1 (best) → 4 (just pass). Roll
            ≤ SC/10 = QR1, ≤ SC/5 = QR2, ≤ SC/2 = QR3, ≤ SC = QR4.
          </li>
          <li className="br-label">
            <strong>Fame</strong> · NPC recognition target — used by the Fame
            Recognition roll when you meet a TAROT NPC for the first time.
          </li>
          <li className="br-label">
            <strong>Hero</strong> · Hero Points reservoir — spend 1 to bump a
            failed roll up by 1 QR step.
          </li>
        </ul>
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Characteristics</h3>
        <dl className="br-scenario-stats-grid">
          {(['STR', 'DEX', 'WIL', 'PER', 'INT'] as const).map((c) => (
            <div key={c}>
              <dt className="br-label">{c}</dt>
              <dd className="br-mono">{stats[c]}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Skills (all)</h3>
        {/* 2026-06-07 audit pass: each row shows PC + SL with correct labels
         *  (pre-audit pc_pregens.json carried {primary, total} which silently
         *  labelled Skill Level as "PC"). The substituted-formula line shows
         *  the derivation against this PC's stats — [(14+12)/2] + 12 = 25 for
         *  Bond Boating — and the form line shows the abstract pattern. */}
        <ul className="br-scenario-skills-list">
          {skillEntries.map(([name, s]) => {
            const sub = substituteSkillFormula(name, s, stats, charismaSkillLevel);
            const slug = name.toLowerCase().replace(/\s+/g, '-');
            return (
              <li key={name} className="br-scenario-skills-row">
                <span className="br-label">{name}</span>
                <span className="br-label">PC {s.primary_chance} · SL {s.skill_level}</span>
                {sub && (
                  <>
                    <span
                      className="br-label br-scenario-skills-formula br-scenario-skills-formula-sub"
                      data-testid={`br-scenario-skills-substituted-${slug}`}
                    >
                      {sub.substituted}
                    </span>
                    <span
                      className="br-label br-scenario-skills-formula"
                      data-testid={`br-scenario-skills-formula-${slug}`}
                    >
                      {sub.form}
                    </span>
                    {!sub.matches && (
                      <span
                        className="br-label br-scenario-skills-formula-mismatch"
                        data-testid={`br-scenario-skills-mismatch-${slug}`}
                        title="Computed PC differs from stored PC — data drift; please file an issue."
                      >
                        ⚠ stored PC {s.primary_chance} ≠ computed {sub.computed}
                      </span>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Combat</h3>
        <p className="br-label">
          Speed {pregen.combat.speed} · H2H DC {pregen.combat.hand_to_hand_damage_class}
        </p>
        <p className="br-label">
          Fame {pregen.combat.fame_points} · Hero {pregen.combat.hero_points}
        </p>
        <p className="br-label br-scenario-stats-help">
          Speed = move bands per round. H2H DC A–F (A is lightest punch, F a
          professional strike). Stamina {pregen.combat.stamina_hours}h ·
          Run/swim {pregen.combat.running_swimming_minutes}min ·
          Carry {pregen.combat.carrying_lbs} lbs.
        </p>
      </section>
    </aside>
  );
}

interface HistoryPanelProps {
  history: number[];
  rolls: RollLog[];
  /** A8: campaign name passed through to the dossier export. */
  missionName?: string;
  /** A8r (2026-06-04): NPCs encountered in this campaign + scenario,
   *  shaped for the dossier exporter. */
  dossierNpcs: DossierNpc[];
  /** A8r (2026-06-04): NPC ids whose Fame Recognition would render
   *  their name unredacted. Slice A auto-recognizes ALL encountered. */
  recognizedNpcIds: ReadonlySet<string>;
  /** A8r Slice D (2026-06-04): NPC ids whose gm_override is currently
   *  NOT NULL. Drives the segmented control's active state +
   *  ManualRecognitionBadge visibility. */
  manuallyOverriddenNpcIds: ReadonlySet<string>;
  /** A8r Slice D (2026-06-04): per-NPC ever-recognized across all
   *  scenarios in this campaign. Surfaces the "prior encounter" badge
   *  + the cross-scenario narrative line. */
  crossScenarioRecognizedNpcIds: ReadonlySet<string>;
  /** A8r C2 (2026-06-04): per-NPC full cross-scenario metadata Map
   *  (first_scenario_id + first_recognized_at + override_count). Drives
   *  the Prior Encounters panel below the Fame Recognition section. */
  crossScenarioRecognitionsByNpc: ReadonlyMap<string, CrossScenarioRecognition>;
  /** A8r Slice C/D (2026-06-04): hook method that POSTs to /fame/override.
   *  Slice D extends the signature to accept null (clear override).
   *  C3 (2026-06-04) extends the signature with opts.silent for batch UI
   *  that needs to suppress per-NPC cinematic firings during bulk update. */
  setFameOverride: (
    npcId: string,
    recognized: boolean | null,
    opts?: { silent?: boolean },
  ) => Promise<void>;
  /** Round 14 (2026-06-06, Sprint 2 §4.4): generic Save / Damage /
   *  Skill Check / Percentile rolls fired via the Other Rolls panel
   *  or the voice force_dice_roll tool. Surfaced in a new "Other
   *  Rolls" section below the existing skill-check rolls list. */
  genericRolls: GenericRollEntry[];
  /** 2026-06-07: GM force-paragraph escape hatch. Lets the GM jump
   *  directly to a paragraph number when the engine is stuck (a
   *  decision branch isn't surfacing the right next paragraph, or
   *  manual override is needed mid-game). Validated 1..max inclusive
   *  against the actual scenario size. */
  onForceParagraph: (n: number) => void;
  /** Highest valid paragraph number in the active scenario — drives the
   *  force-paragraph input's max attribute + range hint. */
  maxParagraph: number;
}

function HistoryPanel({
  history,
  rolls,
  missionName,
  dossierNpcs,
  recognizedNpcIds,
  manuallyOverriddenNpcIds,
  crossScenarioRecognizedNpcIds,
  crossScenarioRecognitionsByNpc,
  setFameOverride,
  genericRolls,
  onForceParagraph,
  maxParagraph,
}: HistoryPanelProps) {
  // 2026-06-07 GM force-paragraph: controlled input + range-validated
  // jump button. Empty string means "not yet entered"; out-of-range or
  // non-numeric disables the button.
  const [forceParagraphInput, setForceParagraphInput] = useState<string>('');
  const forceParagraphParsed = Number.parseInt(forceParagraphInput, 10);
  const forceParagraphValid =
    Number.isInteger(forceParagraphParsed) &&
    forceParagraphParsed >= 1 &&
    forceParagraphParsed <= maxParagraph;
  const handleForceParagraph = () => {
    if (!forceParagraphValid) return;
    onForceParagraph(forceParagraphParsed);
    setForceParagraphInput('');
  };
  // A8: convert RollLog[] to DossierRoll[] shape (mostly identity; null
  // quality_rating on Failure is preserved).
  const dossierRolls = rolls.map((r) => ({
    paragraph: r.paragraph,
    skill_name: r.skill_name,
    ease_factor: r.ease_factor,
    primary_chance: r.primary_chance,
    success_chance: r.success_chance,
    d100_roll: r.d100_roll,
    quality_rating: r.quality_rating,
    quality_name: r.quality_name,
  }));

  // A8r Dossier-Enhancement (2026-06-04) — compute priorEncounters array for
  // the dossier export. Filter TAROT NPCs to those with ever_recognized=true
  // via the C2 metadata Map; pass through scenario id + first-recognized date
  // + override count. Memoized on the underlying inputs so the button doesn't
  // recompute on every parent re-render.
  const priorEncountersForExport = useMemo(
    () =>
      dossierNpcs
        .filter((n) => {
          if (!n.isTarot) return false;
          const meta = crossScenarioRecognitionsByNpc.get(n.id);
          return meta?.ever_recognized === true;
        })
        .map((n) => {
          const meta = crossScenarioRecognitionsByNpc.get(n.id);
          return {
            npcId: n.id,
            npcName: n.name,
            firstScenarioId: meta?.first_scenario_id ?? null,
            firstRecognizedAt: meta?.first_recognized_at ?? null,
            overrideCount: meta?.override_count ?? 0,
          };
        }),
    [dossierNpcs, crossScenarioRecognitionsByNpc],
  );

  // A8r C3 (2026-06-04) — batch override state + handler.
  // batchPending disables all 3 buttons + per-NPC toggles during a
  // concurrent in-flight bulk update. silent:true on each call
  // suppresses per-NPC cinematic — a 6-NPC bulk would otherwise queue
  // 6 cinematics.
  const [batchPending, setBatchPending] = useState<boolean>(false);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState<boolean>(false);
  const batchSetAll = useCallback(
    async (recognized: boolean | null) => {
      if (batchPending) return;
      const tarotIds = dossierNpcs.filter((n) => n.isTarot).map((n) => n.id);
      if (tarotIds.length === 0) return;
      setBatchPending(true);
      try {
        await Promise.all(
          tarotIds.map((id) => setFameOverride(id, recognized, { silent: true })),
        );
      } finally {
        setBatchPending(false);
      }
    },
    [batchPending, dossierNpcs, setFameOverride],
  );

  return (
    <aside
      className="br-scenario-drawer"
      data-testid="br-scenario-drawer-history"
      aria-label="Decision history"
    >
      <header>
        <h2 className="br-display">History</h2>
      </header>
      {/* 2026-06-07: GM force-paragraph escape hatch. When the GM gets
       *  stuck — wrong-branch land, scenario engine bug, manual override
       *  needed — type a paragraph number + click Jump. Range-validated
       *  against scenario.story_paragraphs.length. */}
      <section
        className="br-scenario-drawer-section"
        data-testid="br-scenario-history-force-paragraph"
      >
        <h3 className="br-label">GM: Jump to paragraph</h3>
        <div className="br-scenario-force-paragraph-row">
          <input
            type="number"
            min={1}
            max={maxParagraph}
            step={1}
            className="br-scenario-force-paragraph-input br-label"
            data-testid="br-scenario-force-paragraph-input"
            value={forceParagraphInput}
            onChange={(e) => setForceParagraphInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && forceParagraphValid) {
                e.preventDefault();
                handleForceParagraph();
              }
            }}
            placeholder={`1 — ${maxParagraph}`}
            aria-label="Jump to paragraph number"
          />
          <button
            type="button"
            className="br-scenario-force-paragraph-button br-label"
            data-testid="br-scenario-force-paragraph-button"
            onClick={handleForceParagraph}
            disabled={!forceParagraphValid}
            title={
              forceParagraphValid
                ? `Jump to ¶${forceParagraphParsed}`
                : `Enter a paragraph number between 1 and ${maxParagraph}.`
            }
          >
            Jump
          </button>
        </div>
        <p className="br-label br-scenario-force-paragraph-hint">
          Escape hatch when the engine gets stuck. The history records the
          forced jump just like a normal decision.
        </p>
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Paragraphs visited ({history.length})</h3>
        {history.length > 0 ? (
          <p className="br-label" data-testid="br-scenario-history-path">
            {history.join(' → ')}
          </p>
        ) : (
          <p className="br-label">(none yet)</p>
        )}
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Rolls ({rolls.length})</h3>
        {rolls.length === 0 && <p className="br-label">(none yet)</p>}
        <ul className="br-scenario-rolls-list">
          {rolls.map((r, i) => (
            <li key={i} className="br-label br-scenario-rolls-row">
              <span>
                ¶{r.paragraph >= 1 ? r.paragraph : '–'} · {r.skill_name ?? '(skill)'} · EF {r.ease_factor}
              </span>
              <span>
                {r.d100_roll}/{r.success_chance} → {r.quality_name}
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section
        className="br-scenario-drawer-section"
        data-testid="br-scenario-history-other-rolls"
      >
        <h3 className="br-label">Other Rolls ({genericRolls.length})</h3>
        {genericRolls.length === 0 && <p className="br-label">(none yet)</p>}
        <ul className="br-scenario-rolls-list">
          {genericRolls.map((entry, i) => (
            <li
              key={`${entry.timestamp}-${i}`}
              className="br-label br-scenario-rolls-row"
              data-testid="br-scenario-generic-roll-row"
              data-mode={entry.mode}
            >
              <span>
                [{entry.mode.toUpperCase()}] {entry.notation}
              </span>
              <span>
                = <strong>{entry.total}</strong>
                {entry.breakdown.length > 1 && (
                  <> ({entry.breakdown.join(' + ')})</>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
      {/* A8r Slice C/D — GM force-recognize override section.
          Slice D ships the 3-state segmented control (Natural / Force
          Recog'd / Force Unknown), so the flip-to-opposite footgun from
          Slice C is closed. Each TAROT NPC also gets a ManualRecognitionBadge
          inline (when atmosphere flag is on + gm_override is non-null).
          C3 (2026-06-04) adds batch buttons + a confirmation modal for
          the destructive Clear All path. */}
      {dossierNpcs.some((n) => n.isTarot) && (
        <section
          className="br-scenario-drawer-section"
          data-testid="br-tarot-identification-section"
        >
          <div className="br-batch-override-header">
            <h3 className="br-label">TAROT Identification (GM override)</h3>
            <div className="br-batch-override-actions">
              <button
                type="button"
                className="br-batch-override-btn br-label"
                data-testid="br-batch-override-force-all-identified"
                disabled={batchPending}
                onClick={() => batchSetAll(true)}
              >
                Force All Identified
              </button>
              <button
                type="button"
                className="br-batch-override-btn br-label"
                data-testid="br-batch-override-force-all-unknown"
                disabled={batchPending}
                onClick={() => batchSetAll(false)}
              >
                Force All Unknown
              </button>
              <button
                type="button"
                className="br-batch-override-btn br-batch-override-btn-danger br-label"
                data-testid="br-batch-override-clear-all"
                disabled={batchPending}
                onClick={() => setBatchConfirmOpen(true)}
              >
                Clear All Overrides
              </button>
            </div>
          </div>
          {dossierNpcs
            .filter((n) => n.isTarot)
            .map((n) => (
              <ForceRecognizeToggle
                key={n.id}
                npcId={n.id}
                npcName={n.name}
                recognized={recognizedNpcIds.has(n.id)}
                manuallyOverridden={manuallyOverriddenNpcIds.has(n.id)}
                setOverride={setFameOverride}
              />
            ))}
        </section>
      )}
      {/* A8r Slice D — Fame Recognition narrative section. Per the user-picked
          "maximum scope" decision (LLM context + HistoryPanel + roll_log),
          surface a per-NPC narrative line in the History drawer for every
          encountered TAROT NPC. Cross-scenario carry wins over local
          override, override wins over natural — same priority as
          describeFameRecognition's logic. */}
      {dossierNpcs.some((n) => n.isTarot) && (
        <section
          className="br-scenario-drawer-section"
          data-testid="br-fame-recognition-section"
        >
          <h3 className="br-label">Fame Recognition</h3>
          <ul className="br-fame-recognition-list">
            {dossierNpcs
              .filter((n) => n.isTarot)
              .map((n) => {
                const isManual = manuallyOverriddenNpcIds.has(n.id);
                const isCrossScenario = crossScenarioRecognizedNpcIds.has(n.id);
                const isRecognized = recognizedNpcIds.has(n.id);
                const narrative = describeFameRecognition(
                  n.name,
                  0, // SC unknown at this surface; describe falls back to override/cross paths
                  null, // d100 unknown at this surface
                  isRecognized,
                  isManual ? isRecognized : null,
                  isCrossScenario && !isManual,
                );
                return (
                  <li
                    key={n.id}
                    className="br-label br-fame-recognition-row"
                    data-testid={`br-fame-recognition-row-${n.id}`}
                  >
                    <span>{narrative}</span>
                    <ManualRecognitionBadge visible={isManual} npcName={n.name} />
                  </li>
                );
              })}
          </ul>
        </section>
      )}
      {/* A8r C2 (2026-06-04) — Prior Encounters panel. Shows TAROT NPCs
          carried in from prior scenarios in THIS campaign, with the
          scenario_id + first_recognized date so the GM has context for
          the carry-over. Section renders only if at least one TAROT NPC
          in the current encountered set has an ever_recognized=true row
          in another scenario. Source data from useFameRecognition's
          crossScenarioRecognitions Map (server-aggregated). */}
      {(() => {
        const carried = dossierNpcs.filter((n) => {
          if (!n.isTarot) return false;
          const meta = crossScenarioRecognitionsByNpc.get(n.id);
          return meta?.ever_recognized === true;
        });
        if (carried.length === 0) return null;
        return (
          <section
            className="br-scenario-drawer-section"
            data-testid="br-prior-encounters-section"
          >
            <h3 className="br-label">Prior Encounters</h3>
            <ul className="br-prior-encounters-list">
              {carried.map((n) => {
                const meta = crossScenarioRecognitionsByNpc.get(n.id);
                if (!meta) return null;
                const scenarioLabel = meta.first_scenario_id
                  ? meta.first_scenario_id
                      .replace(/^docs\//, '')
                      .replace(/\.md$/, '')
                  : '(unknown scenario)';
                const dateLabel = meta.first_recognized_at
                  ? meta.first_recognized_at.slice(0, 10)
                  : '(unknown date)';
                const overrideNote =
                  meta.override_count > 0
                    ? ` · ${meta.override_count} GM override${meta.override_count === 1 ? '' : 's'}`
                    : '';
                return (
                  <li
                    key={n.id}
                    className="br-label br-prior-encounters-row"
                    data-testid={`br-prior-encounters-row-${n.id}`}
                  >
                    <span>
                      {n.name} — identified in <code>{scenarioLabel}</code>{' '}
                      on {dateLabel}
                      {overrideNote}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })()}
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Export</h3>
        <ExportDossierButton
          input={{
            missionName: missionName ?? 'Untitled Mission',
            rolls: dossierRolls,
            npcs: dossierNpcs,
            priorEncounters: priorEncountersForExport,
          }}
          recognizedNpcIds={recognizedNpcIds}
        />
      </section>
      {/* A8r C3 (2026-06-04) — Clear All Overrides confirmation modal.
          Apply-style batches (Force All Identified / Force All Unknown)
          are non-destructive and fire without confirmation. Only the
          clear path destroys GM decisions, so it gets a guard. */}
      {batchConfirmOpen && (
        <BatchOverrideConfirm
          title="Clear all GM overrides?"
          message="This removes every gm_override on TAROT NPCs in this scenario. Natural d100 outcomes will be restored. This cannot be undone."
          confirmLabel="Clear All Overrides"
          onConfirm={() => {
            setBatchConfirmOpen(false);
            void batchSetAll(null);
          }}
          onCancel={() => setBatchConfirmOpen(false)}
        />
      )}
    </aside>
  );
}

interface RollDicePanelProps {
  pregen: PCPregen;
  rollSkill: string;
  rollEF: EaseFactor;
  onSkillChange: (s: string) => void;
  onEFChange: (ef: EaseFactor) => void;
  onRoll: () => void;
  lastRoll: RollLog | null;
  /** A6: ref attached to the result section so the chip-dispatch portal
   *  can fly to it from the PC's HP display. Optional — drawer still
   *  renders unaffected when omitted. */
  resultRef?: React.Ref<HTMLElement>;
  /** A6: HP-spend handler + state. Button visible only when canSpend &&
   *  lastRoll != null && quality_rating !== 1. */
  heroPoints?: number;
  canSpendHeroPoint?: boolean;
  onSpendHeroPoint?: () => void;
  /** Round 13 (2026-06-06): launch a generic Save/Damage/Skill-Check roll
   *  that bypasses the PC × EF skill-check pipeline. Parent mounts a
   *  separate DiceRollTumble instance for it; the chip surfaces the
   *  result and the tumble auto-clears itself on settle. */
  onGenericRoll?: (spec: {
    mode: 'save' | 'damage' | 'skill_check';
    notation: string;
  }) => void;
}

const GENERIC_SAVE_OPTIONS: Array<{ label: string; notation: string }> = [
  { label: 'd4', notation: '1d4' },
  { label: 'd6', notation: '1d6' },
  { label: 'd8', notation: '1d8' },
  { label: 'd10', notation: '1d10' },
  { label: 'd12', notation: '1d12' },
  { label: 'd20', notation: '1d20' },
];

function RollDicePanel({
  pregen,
  rollSkill,
  rollEF,
  onSkillChange,
  onEFChange,
  onRoll,
  lastRoll,
  resultRef,
  heroPoints,
  canSpendHeroPoint,
  onSpendHeroPoint,
  onGenericRoll,
}: RollDicePanelProps) {
  const skillNames = Object.keys(pregen.skills);
  const currentSkill = pregen.skills[rollSkill];
  // Round 13 — generic-roll panel local state. Default save d20, damage 2d6.
  const [saveNotation, setSaveNotation] = useState<string>('1d20');
  const [damageNotation, setDamageNotation] = useState<string>('2d6');
  const damageValid = /^\d+d\d+([+-]\d+)?$/i.test(damageNotation.trim());
  // 2026-06-07: pre-roll difficulty preview. Computes Success Chance and
  // the per-QR roll ranges + chance percent so the player can see what
  // they need to roll BEFORE committing. Memoised on skill + EF so a
  // change to either re-derives. Returns null if SC math fails.
  const previewSC = useMemo(() => {
    if (!currentSkill) return null;
    try {
      return computeSuccessChance(currentSkill.primary_chance, rollEF as EaseFactor);
    } catch {
      return null;
    }
  }, [currentSkill, rollEF]);
  const previewBrackets = useMemo(
    () => (previewSC === null ? null : computeQualityRatingBrackets(previewSC)),
    [previewSC],
  );
  return (
    <aside
      className="br-scenario-drawer"
      data-testid="br-scenario-drawer-roll"
      aria-label="Roll dice"
    >
      <header>
        <h2 className="br-display">Roll Dice</h2>
        <p className="br-label">
          Manual reference roll — outcome does not gate decisions (per M3b.6 §4.4 +
          §4.2: player chooses the decision matching their roll).
        </p>
      </header>
      <section className="br-scenario-drawer-section">
        <label className="br-label">
          Skill
          <select
            value={rollSkill}
            onChange={(e) => onSkillChange(e.target.value)}
            data-testid="br-scenario-roll-skill-select"
            className="br-label"
          >
            {skillNames.map((name) => (
              <option key={name} value={name}>
                {name} (PC {pregen.skills[name]!.primary_chance})
              </option>
            ))}
          </select>
        </label>
        <label className="br-label">
          Ease Factor
          <select
            value={rollEF}
            onChange={(e) => onEFChange(e.target.value as EaseFactor)}
            data-testid="br-scenario-roll-ef-select"
            className="br-label"
          >
            {EASE_FACTORS.map((ef) => (
              <option key={ef} value={ef}>
                {ef === '0.5' ? '½' : ef}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="br-scenario-decision-button"
          data-testid="br-scenario-roll-button"
          onClick={onRoll}
          disabled={!currentSkill}
        >
          Roll d100
        </button>
        {/* 2026-06-07: pre-roll difficulty preview. Shows the SC + per-QR
         *  roll ranges + chance percent so the player knows the
         *  difficulty BEFORE rolling. Renders only when skill + EF
         *  produce a valid SC. */}
        {currentSkill && previewSC !== null && previewBrackets !== null && (
          <div
            className="br-scenario-roll-preview"
            data-testid="br-scenario-roll-preview"
          >
            <p className="br-label br-scenario-roll-preview-summary">
              SC {previewSC} · roll 1d100 — lower is better
            </p>
            <ul
              className="br-scenario-roll-preview-brackets"
              data-testid="br-scenario-roll-preview-brackets"
            >
              {previewBrackets.brackets.map((b) => (
                <li
                  key={b.qr}
                  className="br-label br-scenario-roll-preview-bracket"
                  data-qr={b.qr}
                  data-testid={`br-scenario-roll-preview-qr-${b.qr}`}
                >
                  <span className="br-scenario-roll-preview-qr">
                    QR {b.qr} · {b.qrName}
                  </span>
                  <span className="br-scenario-roll-preview-range">
                    {b.low === b.high ? b.low : `${b.low}–${b.high}`}
                  </span>
                  <span className="br-scenario-roll-preview-pct">
                    {b.chancePercent}%
                  </span>
                </li>
              ))}
              <li
                className="br-label br-scenario-roll-preview-bracket br-scenario-roll-preview-failure"
                data-testid="br-scenario-roll-preview-failure"
              >
                <span className="br-scenario-roll-preview-qr">Failure</span>
                <span className="br-scenario-roll-preview-range">
                  {previewBrackets.failureLow === previewBrackets.failureHigh
                    ? previewBrackets.failureLow
                    : `${previewBrackets.failureLow}–${previewBrackets.failureHigh}`}
                </span>
                <span className="br-scenario-roll-preview-pct">
                  {previewBrackets.failureChancePercent}%
                </span>
              </li>
            </ul>
          </div>
        )}
      </section>
      {lastRoll && (
        <section
          ref={resultRef}
          className="br-scenario-drawer-section"
          data-testid="br-scenario-roll-result"
          data-quality-rating={lastRoll.quality_rating ?? 'fail'}
        >
          <h3 className="br-label">Result</h3>
          <p className="br-label">
            Skill: {lastRoll.skill_name} · EF {lastRoll.ease_factor}
          </p>
          <p className="br-label">
            PC {lastRoll.primary_chance} × EF → SC {lastRoll.success_chance}
          </p>
          <p className="br-label">
            d100: {lastRoll.d100_roll} → <strong>{lastRoll.quality_name}</strong>
          </p>
          {onSpendHeroPoint && lastRoll.quality_rating !== 1 && (
            <button
              type="button"
              className="br-scenario-spend-hero-point"
              data-testid="br-scenario-spend-hero-point-button"
              onClick={onSpendHeroPoint}
              disabled={!canSpendHeroPoint}
              title={
                canSpendHeroPoint
                  ? 'Spend a Hero Point to upgrade this roll'
                  : 'No Hero Points remaining'
              }
            >
              Spend [H] ({heroPoints ?? 0})
            </button>
          )}
        </section>
      )}
      {onGenericRoll && (
        <section
          className="br-scenario-drawer-section"
          data-testid="br-scenario-drawer-roll-other"
          aria-label="Other rolls"
        >
          <h3 className="br-label">Other Rolls</h3>
          <p className="br-label">
            Ad-hoc rolls that bypass the PC × EF skill-check pipeline — pure dice + chip.
          </p>
          <label className="br-label">
            Save
            <select
              value={saveNotation}
              onChange={(e) => setSaveNotation(e.target.value)}
              data-testid="br-scenario-roll-save-select"
              className="br-label"
            >
              {GENERIC_SAVE_OPTIONS.map((opt) => (
                <option key={opt.notation} value={opt.notation}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="br-scenario-decision-button"
            data-testid="br-scenario-roll-save-button"
            onClick={() => onGenericRoll({ mode: 'save', notation: saveNotation })}
          >
            Roll Save ({saveNotation})
          </button>
          <label className="br-label">
            Damage notation
            <input
              type="text"
              value={damageNotation}
              onChange={(e) => setDamageNotation(e.target.value)}
              data-testid="br-scenario-roll-damage-input"
              className="br-label"
              placeholder="2d6 or 3d8+2"
              aria-invalid={!damageValid}
            />
          </label>
          <button
            type="button"
            className="br-scenario-decision-button"
            data-testid="br-scenario-roll-damage-button"
            onClick={() => onGenericRoll({ mode: 'damage', notation: damageNotation.trim() })}
            disabled={!damageValid}
            title={damageValid ? '' : 'Use NdM or NdM+K notation (e.g. 2d6, 3d8+2)'}
          >
            Roll Damage ({damageNotation})
          </button>
        </section>
      )}
    </aside>
  );
}

interface AudioPanelProps {
  muted: boolean;
  volume: number;
  currentCueId: string | null;
  unavailable: boolean;
  onToggleMute: () => void;
  onVolumeChange: (next: number) => void;
}

function AudioPanel({
  muted,
  volume,
  currentCueId,
  unavailable,
  onToggleMute,
  onVolumeChange,
}: AudioPanelProps) {
  return (
    <aside
      className="br-scenario-drawer"
      data-testid="br-scenario-drawer-audio"
      aria-label="Audio settings"
    >
      <header>
        <h2 className="br-display">Audio</h2>
        <p className="br-label">
          YouTube-backed atmospheric cues. Per-location ambient bed crossfades
          on scene change.
        </p>
      </header>
      <section className="br-scenario-drawer-section">
        <button
          type="button"
          className="br-scenario-decision-button"
          data-testid="br-audio-mute-toggle"
          aria-pressed={muted}
          onClick={onToggleMute}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
        <label className="br-label">
          Volume {volume}
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={volume}
            disabled={muted}
            onChange={(e) => onVolumeChange(parseInt(e.target.value, 10))}
            data-testid="br-audio-volume-slider"
            className="br-scenario-audio-volume"
          />
        </label>
      </section>
      <section className="br-scenario-drawer-section">
        <h3 className="br-label">Current cue</h3>
        <p className="br-label" data-testid="br-audio-current-cue">
          {currentCueId ?? '(none)'}
        </p>
        {unavailable && (
          <p
            className="br-label br-scenario-audio-unavailable"
            data-testid="br-audio-unavailable"
          >
            Theme music unavailable — cue&apos;s video_id is null or the
            YouTube IFrame API is unreachable.
          </p>
        )}
      </section>
      <VoicePickerSection />
      <MicActivationSection />
      <NarrationModeSection />
    </aside>
  );
}

// ─── Top-level component ────────────────────────────────────────────────

const DEFAULT_STATE: ScenarioRunnerState = {
  schemaVersion: SCENARIO_STATE_SCHEMA_VERSION,
  view: { kind: 'briefing' },
  history: [],
  rolls: [],
};

export interface ScenarioRunnerProps {
  campaign: CampaignMeta;
  pregen: PCPregen;
  onPlayAgain: () => void;
  // D-track polish v1.1 (2026-06-02): user explicit ask — "i want a way
  // to get back to the hero intro screen". Optional so existing tests
  // that don't supply it keep working.
  onBackToBriefingRoom?: () => void;
  // 2026-06-07: theme chooser drawer. Optional so existing tests + the
  // pre-theme-system call path keep compiling.
  themeSelection?: UseThemeSelectionResult;
}

/**
 * ScenarioRunner — M3b.6.
 *
 * Renders the Dr. No adventure as a data-driven CYOA flow. Reads
 * `seed/scenarios/dr_no_adventure.json` at compile time via the `@seed` vite
 * alias and operates over its 102 numbered paragraphs + 4 NPCs + 6 endings.
 *
 * State machine: briefing → paragraph(N) → … → ending(N, type).
 *
 * Per M3b.6 §4.1 (engine math is client-side TS port) + §4.2 (manual roll
 * via drawer panel — auto-roll-from-prose-extraction deferred to M3c) +
 * §4.3 (collapsible drawer for stats / history / roll) + §4.4 (combat
 * outcomes are player-pick CYOA buttons) + §4.5 (off-range refs disabled
 * with ⚠) + §4.6 (localStorage per campaign × pregen) + §4.7 (ending
 * replace-view + Play Again) + §4.8 (synthetic Continue for "Go to ¶X").
 */
export function ScenarioRunner({
  campaign,
  pregen,
  onPlayAgain,
  onBackToBriefingRoom,
  themeSelection,
}: ScenarioRunnerProps) {
  const stateKey = useMemo(
    () => makeStateKey(campaign.id, pregen.id),
    [campaign.id, pregen.id],
  );

  const [state, setState] = useState<ScenarioRunnerState>(
    () => readScenarioState(stateKey) ?? DEFAULT_STATE,
  );
  // 2026-06-05 persistence triple: drawer + lastRoll were React-local and
  // blanked on every reload. Seed both from the persisted scenario state
  // so reopening the app restores the visible side panel + Result panel
  // for the most-recent roll. readScenarioState is a cheap localStorage
  // read + JSON.parse so three lazy-init calls per mount is fine.
  const [drawer, setDrawer] = useState<DrawerSection>(
    () => readScenarioState(stateKey)?.drawer ?? null,
  );
  // V7.2.B (2026-06-03) — PDF library manifest fetch + viewer modal state
  const pdfLibrary = usePdfLibrary();
  const modulesLibrary = useModulesLibrary();
  // 2026-06-08 Phase 1E — Q Manual VG35002 weapons catalog drawer.
  const equipmentLibrary = useEquipmentLibrary();
  const vehiclesLibrary = useVehiclesLibrary();
  const gadgetsLibrary = useGadgetsLibrary();
  const drugsLibrary = useDrugsLibrary();
  const qBranchPersonnelLibrary = useQBranchPersonnelLibrary();
  const npcArchetypesLibrary = useNpcArchetypesLibrary();
  const canonicalNpcsLibrary = useCanonicalNpcsLibrary();
  const villainsLibrary = useVillainsLibrary();
  const thrillingLocationsLibrary = useThrillingLocationsLibrary();
  const [pdfModal, setPdfModal] = useState<{ path: string; title: string } | null>(null);
  const firstSkill = Object.keys(pregen.skills)[0] ?? '';
  const [rollSkill, setRollSkill] = useState<string>(firstSkill);
  const [rollEF, setRollEF] = useState<EaseFactor>('5');
  const [lastRoll, setLastRoll] = useState<RollLog | null>(
    () => {
      const rolls = readScenarioState(stateKey)?.rolls;
      return rolls && rolls.length > 0 ? rolls[rolls.length - 1] : null;
    },
  );
  // A2 (2026-06-03): pendingRoll holds the resolved RollLog DURING the
  // DiceRollTumble animation; the tumble's onComplete callback promotes it
  // to lastRoll so the Result panel reveals when the dice settle.
  const [pendingRoll, setPendingRoll] = useState<RollLog | null>(null);
  // 2026-06-06 round 13: generic roll spec — Save / Damage / Skill Check
  // modes that bypass the skill-check (percentile) PC × EF pipeline. Set
  // by Other-Roll buttons in the RollDicePanel; consumed by a separate
  // DiceRollTumble instance that surfaces the result via its own chip.
  // No RollLog, no quality rating, no QR cinematic — purely the dice +
  // chip result. Cleared by the tumble's onComplete.
  const [pendingGenericRoll, setPendingGenericRoll] = useState<
    {
      mode: 'save' | 'damage' | 'skill_check' | 'percentile';
      notation: string;
      // Sprint 4 §4.5 (2026-06-06) — fix for dice-accumulation bug seen on
      // 1.300.0. DiceRollTumble's create-once useEffect (round 10 discipline)
      // means a pendingGenericRoll {a} → {b} state transition where both are
      // truthy DOES NOT remount the component, the dice-box doesn't re-init,
      // and the OLD dice stay on the canvas. Symptom: asked for 3d6, see
      // dice from the previous roll persist alongside the new ones — the
      // user-reported "your sum calc thing is repeating multiple times" with
      // breakdown=[10] (a leftover d10) when 3d6 was requested.
      //
      // The fix: include a monotonic instanceId here. The JSX consumer uses
      // it as the `key`, which forces a full unmount+remount on every new
      // roll → fresh dice-box instance → clean scene every time. Two rolls
      // with identical mode+notation still get different instanceIds. */
      instanceId: number;
    } | null
  >(null);
  const nextGenericRollInstanceId = useRef(0);
  // Wrap setPendingGenericRoll so consumers don't have to remember to stamp
  // the instanceId — they pass the {mode, notation} pair, we stamp it.
  const setPendingGenericRollWithId = useCallback(
    (spec: { mode: 'save' | 'damage' | 'skill_check' | 'percentile'; notation: string } | null) => {
      if (spec === null) {
        setPendingGenericRoll(null);
        return;
      }
      nextGenericRollInstanceId.current += 1;
      setPendingGenericRoll({ ...spec, instanceId: nextGenericRollInstanceId.current });
    },
    [],
  );
  // Round 14 (2026-06-06, Sprint 2 §4.4): generic Save/Damage/Skill
  // Check/Percentile roll history. Seeded from persisted state.
  const [genericRolls, setGenericRolls] = useState<GenericRollEntry[]>(
    () => readScenarioState(stateKey)?.genericRolls ?? [],
  );
  // Round 14 (2026-06-06, Sprint 2 §4.3): chip-fly portal state. The
  // sourcePoint is captured BEFORE the tumble unmounts (via the
  // onChipReady callback on the generic-roll DiceRollTumble) so the
  // portal can fly from a static screen position even after the chip
  // element is gone. The portal flies to historyToggleRef (the History
  // drawer toggle button); user choice 2026-06-06 was to skip the fly
  // on percentile mode (QR cinematic IS the visual reveal there).
  const [pendingChipFly, setPendingChipFly] = useState<
    {
      sourcePoint: { x: number; y: number };
      result: { mode: 'save' | 'damage' | 'skill_check' | 'percentile'; total: number; breakdown: number[] };
      notation: string;
    } | null
  >(null);
  // Round 14 (Sprint 2 §4.3): bridge ref between the generic tumble's
  // onResult (fires inside dice-box onRollComplete) and onChipReady
  // (fires when the chip ref callback runs post-render). onResult stashes
  // the latest result here; onChipReady reads it to compose the portal
  // payload. Ref-based bridge avoids the React-batching race where
  // setGenericRolls's update isn't yet visible when onChipReady fires.
  const lastGenericResultRef = useRef<{
    mode: 'save' | 'damage' | 'skill_check' | 'percentile';
    total: number;
    breakdown: number[];
  } | null>(null);
  const historyToggleRef = useRef<HTMLButtonElement | null>(null);
  // A3 (2026-06-03): qrPending holds the RollLog DURING the QRCinematicResponse
  // animation that fires after the tumble settles. handleQRComplete clears
  // it + ends the cinematic store. Kept separate from pendingRoll so the
  // tumble can fully unmount before the QR card mounts (avoids overlap).
  const [qrPending, setQrPending] = useState<RollLog | null>(null);
  // Workstream A 2026-06-08: when a skill_check decision edge is clicked,
  // we store the edge here so handleQRComplete can look up the correct
  // outcome paragraph after the QR cinematic settles. Cleared immediately
  // after routing (or on play-again reset).
  const [pendingSkillCheckEdge, setPendingSkillCheckEdge] = useState<SkillCheckEdge | null>(null);
  // 2026-06-05 stacking fix (cinematic-linger follow-up): backlog of QR
  // results queued behind the currently-displayed qrPending. New rolls
  // that resolve while a cinematic is on screen (voice dispatch path or
  // manual-dismiss mode + a queued button click) append here instead of
  // silently overwriting qrPending mid-display. handleQRComplete pops
  // the head on dismiss.
  const [qrQueue, setQrQueue] = useState<RollLog[]>([]);
  // Bumped whenever qrPending transitions to a new RollLog (initial mount,
  // queue-pop, or HP upgrade re-fire) so React tears down + remounts the
  // QRCinematicResponse with a fresh entrance animation per result.
  const [cinematicMountId, setCinematicMountId] = useState(0);
  // A6 (2026-06-04): Hero Point spend cinematic. chipFlying carries the
  // RollLog currently mid-upgrade — the chip portal renders while this is
  // set; onComplete promotes it to the upgraded QR and re-fires qrPending.
  const [chipFlying, setChipFlying] = useState<RollLog | null>(null);
  const heroPointSourceRef = useRef<HTMLSpanElement | null>(null);
  const rollResultTargetRef = useRef<HTMLElement | null>(null);
  // A6e (2026-06-03): server-backed HP ledger replaces A6 local-state shim.
  // Hook contract preserved ({heroPoints, spend, canSpend}); new error
  // field consumed below via console.warn so server sync failures surface
  // in devtools without requiring a toast UI (deferred to future PR).
  const {
    heroPoints,
    spend: spendHeroPoint,
    canSpend: canSpendHeroPoint,
    error: heroPointError,
  } = useHeroPointSpend({
    campaignId: campaign.id,
    initialPoints: pregen.combat.hero_points,
  });
  useEffect(() => {
    if (heroPointError !== null) {
      console.warn('[A6e] Hero point sync failed:', heroPointError);
    }
  }, [heroPointError]);
  // A2 Slice B: cinematic mode (selector vs voice) + voice intent for the
  // transcript bubble. The store is the cross-component source of truth so
  // future A3 QR cinematics + voice-confirmation can subscribe to the same
  // state without prop drilling.
  const cinematicMode = useRollCinematicStore((s) => s.mode);
  const cinematicVoiceIntent = useRollCinematicStore((s) => s.voiceIntent);
  const startSelectorCinematic = useRollCinematicStore((s) => s.startSelector);
  const startVoiceCinematic = useRollCinematicStore((s) => s.startVoice);
  const endCinematic = useRollCinematicStore((s) => s.end);

  // D-5: audio state. Volume / mute persist across sessions; the player
  // ref + current-cue ref live for the component lifetime only.
  const voiceLog = useVoiceLog(campaign.id);
  // V4 Voice GM (2026-06-02): shared TTS queue that drives:
  //   - paragraph autoplay (via useAutoNarrate below)
  //   - V3 GM rationale (via useGMDispatcher's ttsQueue callback)
  //   - ReadAloud replay button (via TtsQueueContext)
  //   - Mic press cancel-on-start (via TtsQueueContext)
  const ttsQueue = useTtsQueue();
  // dispatcher (declared below) handles the player-turn voice log append
  // itself, so this callback just kicks off the full dispatch loop.
  // Defined as a stable ref-forwarder so MicControl's prop identity is
  // stable across renders; the dispatcher closure is updated below.
  const dispatcherRef = useRef<{
    dispatchTranscript: (text: string) => Promise<unknown>;
  } | null>(null);
  // A8r C4 (2026-06-04): forward ref for setFameOverride. useFameRecognition
  // is called AFTER useGMDispatcher in this component (the fame hook depends
  // on encounteredNpcIds which are computed in between). The dispatcher's
  // onFameOverride callback closes over THIS ref, and we assign ref.current
  // below the useFameRecognition destructure so the closure resolves at
  // call-time, not render-time.
  const setFameOverrideRef = useRef<
    | ((npcId: string, recognized: boolean | null, opts?: { silent?: boolean }) => Promise<void>)
    | null
  >(null);
  // A2 Slice B (2026-06-03): hold the most-recent player utterance so that
  // when the dispatcher returns a roll_skill tool call, we can render it in
  // the DiceRollTumble transcript bubble.
  const lastTranscriptRef = useRef<string | null>(null);
  const handleTranscript = useCallback((text: string) => {
    lastTranscriptRef.current = text;
    void dispatcherRef.current?.dispatchTranscript(text);
  }, []);

  const [audioMuted, setAudioMuted] = useState<boolean>(() =>
    readMutePreference(),
  );
  const [audioVolume, setAudioVolume] = useState<number>(() =>
    readVolumePreference(),
  );
  const [audioUnavailable, setAudioUnavailable] = useState<boolean>(false);
  const [currentCueId, setCurrentCueId] = useState<string | null>(null);
  const audioPlayerRef = useRef<YouTubeTheaterPlayer | null>(null);
  const currentCueIdRef = useRef<string | null>(null);

  useEffect(() => {
    // 2026-06-05 persistence triple: drawer is kept as separate React state
    // (minimal refactor — 8 toggleDrawer call sites stay unchanged) but
    // mirrored into the persisted ScenarioRunnerState here so a single
    // writeScenarioState call captures everything.
    // Round 14 (2026-06-06, Sprint 2 §4.4): mirror genericRolls too.
    writeScenarioState(stateKey, { ...state, drawer, genericRolls });
  }, [stateKey, state, drawer, genericRolls]);

  // D-5: compute target cue for the current view. Unmapped paragraphs
  // return null — caller treats that as "keep current cue playing".
  const targetCueId = useMemo<string | null>(() => {
    if (state.view.kind === 'briefing') return BRIEFING_CUE_ID;
    if (state.view.kind === 'ending') {
      return ENDING_TO_STING_CUE_ID[state.view.ending_type] ?? null;
    }
    if (state.view.kind === 'paragraph') {
      const locationId = PARAGRAPH_SCENE_MAP[state.view.number];
      if (locationId !== undefined) return LOCATION_TO_CUE_ID[locationId];
    }
    return null;
  }, [state.view]);

  // D-5: cue transitions. First non-null target triggers play(); subsequent
  // changes trigger crossfadeTo(). Muted state suppresses all calls until
  // unmuted (see the mute effect below for resume).
  useEffect(() => {
    if (audioMuted) return;
    if (targetCueId === null) return;
    if (targetCueId === currentCueIdRef.current) return;

    if (audioPlayerRef.current === null) {
      audioPlayerRef.current = new YouTubeTheaterPlayer({
        cueMap: youtubeCues as YouTubeCueMap,
        onUnavailable: () => setAudioUnavailable(true),
      });
    }
    const player = audioPlayerRef.current;
    const isFirstPlay = currentCueIdRef.current === null;
    currentCueIdRef.current = targetCueId;
    setCurrentCueId(targetCueId);

    if (isFirstPlay) {
      void player.play(targetCueId);
    } else {
      const fadeMs =
        state.view.kind === 'ending' ? STING_CROSSFADE_MS : AMBIENT_FADE_MS;
      void player.crossfadeTo(targetCueId, fadeMs);
    }
  }, [targetCueId, audioMuted, state.view.kind]);

  // D-5: mute / unmute ducks volume to 0 / restores to the current cue's
  // configured volume. The player ref may not exist yet on first mute (no
  // cue ever played) — that path is a no-op via the player's own guard.
  useEffect(() => {
    const player = audioPlayerRef.current;
    if (player === null) return;
    if (audioMuted) {
      void player.duck(0, MUTE_FADE_MS);
    } else if (currentCueIdRef.current !== null) {
      const cue = (youtubeCues as YouTubeCueMap).cues[currentCueIdRef.current];
      const targetVolume = Math.round(
        ((cue?.volume ?? 60) * audioVolume) / 100,
      );
      void player.duck(targetVolume, MUTE_FADE_MS);
    }
  }, [audioMuted, audioVolume]);

  // D-5: cleanup — fade-out on unmount so the cue doesn't bleed into the
  // post-scenario campaign picker.
  useEffect(() => {
    return () => {
      const player = audioPlayerRef.current;
      if (player !== null) {
        void player.fadeOut(UNMOUNT_FADE_MS);
      }
    };
  }, []);

  const handleToggleMute = useCallback(() => {
    setAudioMuted((current) => {
      const next = !current;
      writeMutePreference(next);
      return next;
    });
  }, []);

  const handleVolumeChange = useCallback((next: number) => {
    setAudioVolume(next);
    writeVolumePreference(next);
  }, []);

  const goToParagraph = useCallback((n: number) => {
    const para = findParagraph(n);
    if (!para) return;
    const view: RunnerView =
      para.is_ending && para.ending_type && !para.is_partial_ending
        ? { kind: 'ending', paragraph: n, ending_type: para.ending_type }
        : { kind: 'paragraph', number: n };
    setState((prev) => ({
      ...prev,
      view,
      history: [...prev.history, n],
    }));
  }, []);

  // V7.2.A (2026-06-02): shared executor so the Roll drawer + the inline
  // QuickRollButton both fire the same path. Skill key matching is
  // case-insensitive against pregen.skills to bridge scenario prose
  // ("Evasion") to pregen sheet keys ("evasion").
  const executeRoll = useCallback(
    (skillKey: string, ef: string): boolean => {
      // 2026-06-04 dice-roll-feedback fix: detector skill names from the
      // 1983 Victory Games prose ("Perception", "Hand-to-Hand", "Pilot",
      // "Lockpicking") don't match the pregen sheet keys ("Sixth Sense",
      // "Hand-to-Hand Combat", "Piloting", "Lockpicking and
      // Safecracking"). resolveSkillToPregenKey tries the direct match
      // first, then the SKILL_ALIASES map.
      const normalised = resolveSkillToPregenKey(
        skillKey,
        Object.keys(pregen.skills),
      );
      if (normalised === null) return false;
      const skill = pregen.skills[normalised];
      if (!skill) return false;
      // 2026-06-07 audit fix: pre-rename, the seed labelled fields {primary,
      // total} but the values were actually (Skill Level, Primary Chance) per
      // Core Rulebook p.16-17 line 730. Pre-fix used skill.primary (Skill
      // Level) here — every roll was rolling against SkillLevel × EF instead
      // of PrimaryChance × EF. Bond Boating: SC was 12 × 5 = 60; correct is
      // 25 × 5 = 125. See feedback_skill_formula_substitution_audit.md.
      const pc = skill.primary_chance;
      if (pc < 1) return false;
      if (!(EASE_FACTORS as readonly string[]).includes(ef)) return false;
      const result = resolveTask(pc, ef as EaseFactor);
      const log: RollLog = {
        paragraph:
          state.view.kind === 'paragraph' ? state.view.number : -1,
        skill_name: normalised,
        ease_factor: ef,
        primary_chance: pc,
        success_chance: result.success_chance,
        d100_roll: result.d100_roll,
        quality_rating: result.quality_rating,
        quality_name: result.quality_name,
      };
      // A2: defer the result reveal — DiceRollTumble plays for ~1.2s then
      // its onComplete promotes pendingRoll → lastRoll. The history array
      // updates immediately so the rolls drawer + state persistence stay
      // truthful even if the user navigates away mid-tumble.
      // Slice B: selector-trigger path — flip the cinematic store into
      // selector mode so DiceRollTumble renders without the transcript bubble.
      startSelectorCinematic();
      setLastRoll(null);
      setPendingRoll(log);
      setState((prev) => ({ ...prev, rolls: [...prev.rolls, log] }));
      return true;
    },
    [pregen.skills, state.view, startSelectorCinematic],
  );

  // A2 (2026-06-03): tumble settle handler. Promotes pendingRoll → lastRoll
  // so the Result panel reveals on the dice-settle frame. A3 (2026-06-03):
  // also promotes pendingRoll → qrPending so the QR cinematic mounts and
  // plays. endCinematic is deferred to handleQRComplete so the cinematic
  // store stays in selector/voice mode through the QR response (the QR
  // card reads `mode` from the store for voice-mode differentials).
  const handleTumbleComplete = useCallback(() => {
    setPendingRoll((pending) => {
      if (pending !== null) {
        setLastRoll(pending);
        // 2026-06-05 stacking fix: if a QR cinematic is already on screen,
        // queue this result instead of overwriting the live mount. The
        // pre-fix `setQrPending(pending)` was an unconditional overwrite
        // that erased the previous outcome silently — caught by Track 2
        // of the cinematic-linger live validation.
        setQrPending((current) => {
          if (current === null) {
            setCinematicMountId((n) => n + 1);
            return pending;
          }
          setQrQueue((q) => [...q, pending]);
          return current;
        });
      }
      return null;
    });
  }, []);

  // 2026-06-08 F5e fix — single-RNG invariant patcher for skill-check rolls.
  //
  // Pre-fix flow: ScenarioRunner called resolveTask(pc, ef) which internally
  // ran defaultRoll() (Math.random) producing random#1. pendingRoll was set
  // with random#1's d100_roll + the matching QR. Then DiceRollTumble spun up
  // dice-box physics which produced random#2 as the visible `total` on the
  // chip. The chip showed total (random#2) but called its verdict from
  // pendingRoll.quality_rating (random#1) — two independent rolls glued
  // together. Persisted state.rolls also held random#1.
  //
  // Post-fix: SkillCheckInterpretation recomputes the verdict from `total` +
  // SC inline (so the chip shows a coherent total + verdict + tier-highlight),
  // AND fires this callback at chip-mount time so persisted state matches
  // what the player saw. We patch BOTH pendingRoll AND the last entry of
  // state.rolls (which handleSkillRoll appended at trigger time). The last-
  // entry assumption holds because no other roll can land between dice settle
  // and this callback firing (the cinematic store is single-track per-roll).
  const handleVerdictResolved = useCallback(
    (verdict: { total: number; qualityRating: number | null; qualityName: string }) => {
      setPendingRoll((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              d100_roll: verdict.total,
              quality_rating: verdict.qualityRating,
              quality_name: verdict.qualityName,
            },
      );
      setState((prev) => {
        if (prev.rolls.length === 0) return prev;
        const lastIdx = prev.rolls.length - 1;
        const patched: RollLog = {
          ...prev.rolls[lastIdx],
          d100_roll: verdict.total,
          quality_rating: verdict.qualityRating,
          quality_name: verdict.qualityName,
        };
        return {
          ...prev,
          rolls: [...prev.rolls.slice(0, lastIdx), patched],
        };
      });
    },
    [],
  );

  // A3 (2026-06-03): QR cinematic settle handler. 2026-06-05 stacking
  // update: pops the next queued RollLog (if any) so sequential dismiss
  // walks the backlog before clearing the cinematic store + tearing the
  // overlay down. Empty queue → same legacy behavior (clear + endCinematic).
  //
  // Workstream A 2026-06-08: when the queue is empty AND pendingSkillCheckEdge
  // is set, look up the outcome paragraph from the edge's `outcomes` map
  // (keyed by the settled QR tier or "failure") and call goToParagraph.
  // lastRoll holds the F5e-patched QR at this point (handleVerdictResolved
  // has already fired). We consume pendingSkillCheckEdge atomically: clear it
  // before routing so a navigation error can't leave a stale edge behind.
  const handleQRComplete = useCallback(() => {
    setQrQueue((q) => {
      if (q.length > 0) {
        // 2026-06-05 stacking: pop head → mount as next active cinematic.
        // Do NOT setLastRoll here — handleTumbleComplete already set it
        // when each queued roll resolved; setting it here would backwards-
        // step the result panel if a 3rd roll has already tumbled.
        const [head, ...rest] = q;
        setQrPending(head);
        setCinematicMountId((n) => n + 1);
        return rest;
      }
      setQrPending(null);
      endCinematic();
      // Workstream A: route via skill_check edge outcomes (queue-empty branch).
      setPendingSkillCheckEdge((edge) => {
        if (edge !== null) {
          setLastRoll((roll) => {
            if (roll !== null) {
              const qr = roll.quality_rating; // null = Failure
              const target =
                qr === null
                  ? edge.outcomes.failure
                  : qr === 1
                    ? edge.outcomes.qr1
                    : qr === 2
                      ? edge.outcomes.qr2
                      : qr === 3
                        ? edge.outcomes.qr3
                        : edge.outcomes.qr4;
              goToParagraph(target);
            }
            return roll;
          });
        }
        return null; // clear the pending edge
      });
      return q;
    });
  }, [endCinematic, goToParagraph]);

  // A6 (2026-06-04): Hero Point spend handler. Called from the Result
  // panel "Spend [H]" button. Decrements the local HP ledger and arms
  // the chip-portal animation. The QR upgrade happens in
  // handleChipAnimationComplete after the chip lands on the result card.
  const handleSpendHeroPoint = useCallback(() => {
    if (!lastRoll || !canSpendHeroPoint || lastRoll.quality_rating === 1) {
      return;
    }
    spendHeroPoint();
    setChipFlying(lastRoll);
  }, [lastRoll, canSpendHeroPoint, spendHeroPoint]);

  // A6: chip portal settle handler. Computes the upgraded roll (QR - 1,
  // or Failure → QR 4 / Acceptable) and re-fires the QR cinematic at
  // the new level. lastRoll is also updated so the Result panel reflects
  // the upgrade once the cinematic settles.
  const handleChipAnimationComplete = useCallback(() => {
    setChipFlying((flying) => {
      if (!flying) return null;
      const currentQR = flying.quality_rating;
      // null (Failure) upgrades to QR 4 (Acceptable). 2/3/4 upgrades to
      // QR - 1. QR 1 should never reach here — the button is hidden.
      let upgradedQR: 1 | 2 | 3 | 4;
      if (currentQR === null) {
        upgradedQR = 4;
      } else if (currentQR === 2 || currentQR === 3 || currentQR === 4) {
        upgradedQR = (currentQR - 1) as 1 | 2 | 3;
      } else {
        // QR 1 or unexpected value — bail out without upgrading.
        return null;
      }
      const upgradedRoll: RollLog = {
        ...flying,
        quality_rating: upgradedQR,
        quality_name: QR_NAMES[upgradedQR],
      };
      setLastRoll(upgradedRoll);
      setQrPending(upgradedRoll);
      // 2026-06-05 stacking fix: bump mount id so the upgraded cinematic
      // gets a fresh entrance animation rather than reusing the prior
      // mount's settled DOM tree.
      setCinematicMountId((n) => n + 1);
      return null;
    });
  }, []);

  // Workstream A 2026-06-08: skill_check decision edge click handler.
  // Stores the edge (for post-QR routing in handleQRComplete) then fires
  // the roll. executeRoll returns false when the skill is unresolvable
  // (e.g. "Strength" not in pregen sheet); in that case we don't store
  // the edge so the player isn't left waiting for a cinematic that won't
  // arrive. The EaseFactor is numeric on the edge but executeRoll expects
  // the string form (e.g. "3") matching EASE_FACTORS.
  const handleSkillCheckEdge = useCallback(
    (edge: SkillCheckEdge) => {
      const efString = String(edge.easeFactor);
      const rolled = executeRoll(edge.skillName, efString);
      if (rolled) {
        setPendingSkillCheckEdge(edge);
      }
    },
    [executeRoll],
  );

  const handleRollDice = useCallback(() => {
    executeRoll(rollSkill, rollEF);
  }, [executeRoll, rollSkill, rollEF]);

  const handleQuickRoll = useCallback(
    (skill: string, ef: string) => {
      executeRoll(skill, ef);
    },
    [executeRoll],
  );

  const handlePlayAgain = useCallback(() => {
    clearScenarioState(stateKey);
    setLastRoll(null);
    setPendingSkillCheckEdge(null);
    onPlayAgain();
  }, [stateKey, onPlayAgain]);

  const toggleDrawer = useCallback((section: DrawerSection) => {
    setDrawer((current) => (current === section ? null : section));
  }, []);

  const currentParagraph = useMemo(
    () =>
      state.view.kind === 'paragraph' ? findParagraph(state.view.number) : null,
    [state.view],
  );

  // V3 Voice GM (2026-06-02): build a DispatchView snapshot for the
  // GMOrchestrator + history slice for context. The label list mirrors
  // what the UI renders so the LLM picks from EXACTLY what the player
  // can see.
  const currentDispatchView = useMemo<DispatchView>(() => {
    if (state.view.kind === 'briefing') {
      const cb = scenario.character_briefing;
      return {
        kind: 'briefing',
        paragraph_text: cb.briefing_text_verbatim,
        // 2026-06-08 Fix #A — the dispatch view's decision_labels list
        // was hardcoded to the dr_no briefing's "Wait for Ben Gazi" /
        // "Search the room" options. That hardcode survived the
        // first_decision_paragraphs object→array generalization, so on
        // VG35014 / Diamonds / Code Red briefing screens the voice GM
        // received the wrong labels and keyword-match dispatch silently
        // failed for non-dr_no scenarios. Source the labels from the
        // active scenario's first_decision_paragraphs[] directly.
        decision_labels: cb.first_decision_paragraphs.map((d) => d.label),
      };
    }
    if (state.view.kind === 'paragraph' && currentParagraph) {
      const labels: string[] = [];
      if (currentParagraph.decisions) {
        for (const d of currentParagraph.decisions) {
          labels.push(d.label);
        }
      }
      if (currentParagraph.narrative_decisions) {
        for (const d of currentParagraph.narrative_decisions) {
          labels.push(d.label);
        }
      }
      return {
        kind: 'paragraph',
        paragraph_number: currentParagraph.number,
        paragraph_text: currentParagraph.text,
        decision_labels: labels,
      };
    }
    return { kind: 'ending', decision_labels: [] };
  }, [state.view, currentParagraph]);

  const dispatchHistory = useMemo<DispatchHistoryTurn[]>(
    () =>
      voiceLog.entries.slice(-6).map((e) => ({
        role: e.role,
        text: e.text,
      })),
    [voiceLog.entries],
  );

  const pregenSkillsScalar = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const [name, val] of Object.entries(pregen.skills)) {
      // 2026-06-07: dispatch sees Primary Chance (the d100 target), not the
      // Skill Level training rank. Pre-rename this read val.primary (=Skill
      // Level) which gave the backend a confusingly small ranking value.
      out[name] = val.primary_chance;
    }
    return out;
  }, [pregen.skills]);

  const onPickDecisionByLabel = useCallback(
    (label: string): boolean => {
      // Look up the label in the briefing first, then the current paragraph's
      // decisions, then narrative_decisions. Return false if not found.
      const trimmed = label.trim();
      if (state.view.kind === 'briefing') {
        const cb = scenario.character_briefing;
        const lower = trimmed.toLowerCase();
        // Match against the briefing decision labels generically. Two
        // strategies in order: (1) exact "A. ..." / "B. ..." letter prefix
        // ("A", "a", "option a", etc.); (2) keyword overlap — find the
        // decision whose label (minus "A./B." prefix + punctuation) shares
        // the most words with the player's transcribed speech.
        const letterMatch = /^(?:option\s+)?([abcd])\b/.exec(lower);
        if (letterMatch) {
          const idx = letterMatch[1]!.charCodeAt(0) - 97;
          const d = cb.first_decision_paragraphs[idx];
          // skill_check edges in first_decision_paragraphs are not expected,
          // but guard defensively: only navigate for NarrativeEdge.
          const target = d && d.kind !== 'skill_check' ? d.leads_to_paragraph : null;
          if (typeof target === 'number') {
            goToParagraph(target);
            return true;
          }
        }
        const tokens = lower.split(/[^a-z0-9]+/u).filter((t) => t.length > 2);
        let best = { idx: -1, score: 0 };
        cb.first_decision_paragraphs.forEach((d, i) => {
          const stripped = d.label.replace(/^[A-Z]\.\s*/, '').toLowerCase();
          const labelTokens = stripped.split(/[^a-z0-9]+/u).filter((t) => t.length > 2);
          const score = tokens.filter((t) => labelTokens.includes(t)).length;
          if (score > best.score) best = { idx: i, score };
        });
        if (best.score > 0) {
          const d = cb.first_decision_paragraphs[best.idx];
          const target = d && d.kind !== 'skill_check' ? d.leads_to_paragraph : null;
          if (typeof target === 'number') {
            goToParagraph(target);
            return true;
          }
        }
        return false;
      }
      if (currentParagraph) {
        const pools: ScenarioDecisionEdge[] = [
          ...(currentParagraph.decisions ?? []),
          ...(currentParagraph.narrative_decisions ?? []),
        ];
        // Voice dispatch only routes narrative edges by label. skill_check
        // edges require a roll — they can't be voice-triggered by label match.
        const match = pools.find(
          (d) =>
            d.kind !== 'skill_check' &&
            d.label.trim().toLowerCase() === trimmed.toLowerCase(),
        );
        const lp = match && match.kind !== 'skill_check' ? match.leads_to_paragraph : null;
        if (match && typeof lp === 'number' && isInRangeDecision(match)) {
          goToParagraph(lp);
          return true;
        }
      }
      return false;
    },
    [state.view, currentParagraph, goToParagraph],
  );

  const onRollSkillFromTool = useCallback(
    (skillName: string, easeFactor: string): boolean => {
      const skill = pregen.skills[skillName];
      if (!skill) {
        return false;
      }
      const ef = EASE_FACTORS.find((e) => e === easeFactor) ?? '5';
      setRollSkill(skillName);
      setRollEF(ef);
      // handleRollDice closes over rollSkill/rollEF via deps; schedule the
      // roll for the next frame so the state writes commit first.
      window.setTimeout(() => {
        // 2026-06-07 audit fix: see executeRoll's matching comment. Pre-fix
        // this read skill.primary (Skill Level) for force_dice_roll voice
        // tool path; same SC-too-low gameplay bug.
        const pc = skill.primary_chance;
        if (pc < 1) return;
        const result = resolveTask(pc, ef);
        const log: RollLog = {
          paragraph:
            state.view.kind === 'paragraph' ? state.view.number : -1,
          skill_name: skillName,
          ease_factor: ef,
          primary_chance: pc,
          success_chance: result.success_chance,
          d100_roll: result.d100_roll,
          quality_rating: result.quality_rating,
          quality_name: result.quality_name,
        };
        // A2 Slice B (2026-06-03): voice-trigger path — defer the result
        // reveal behind the tumble (same as selector path) AND flip the
        // cinematic store into voice mode so the transcript bubble renders
        // with the player's utterance. lastTranscriptRef carries the most
        // recent player text from handleTranscript.
        startVoiceCinematic(lastTranscriptRef.current);
        setLastRoll(null);
        setPendingRoll(log);
        setState((prev) => ({ ...prev, rolls: [...prev.rolls, log] }));
      }, 0);
      return true;
    },
    [pregen.skills, state.view, startVoiceCinematic],
  );

  const onUndoView = useCallback(
    (previousView: DispatchView) => {
      if (previousView.kind === 'briefing') {
        setState((prev) => ({
          ...prev,
          view: { kind: 'briefing' },
          history: prev.history.slice(0, -1),
        }));
      } else if (previousView.kind === 'paragraph' && typeof previousView.paragraph_number === 'number') {
        setState((prev) => ({
          ...prev,
          view: { kind: 'paragraph', number: previousView.paragraph_number! },
          history: prev.history.slice(0, -1),
        }));
      }
    },
    [],
  );

  // V5 (2026-06-02): map per-campaign state into the dispatch payload so the
  // GM sees the player's run-so-far. RollLog → {skill, outcome} keeps the
  // backend contract small + scenario-agnostic.
  const v5StateRolls = useMemo(
    () =>
      state.rolls.map((r) => ({
        skill: r.skill_name ?? 'unknown',
        outcome: r.quality_name,
      })),
    [state.rolls],
  );

  // V9.2.x.2 (2026-06-06) — 11 pending-card state slots for the V9.1
  // dispatch tool surfaces. Each callback in useGMDispatcher sets its
  // slot; later same-tool dispatches REPLACE the previous card. No per-
  // card auto-dismiss in this commit; cards persist across paragraph
  // navigation mirroring the dispatch log discipline.
  //
  // PLACEMENT: useState declarations MUST precede the useGMDispatcher
  // call below so the inline callbacks can close over the setters.
  // (The existing setFameOverride is wired via a forward-ref because
  // it's the output of useFameRecognition which is declared LATER; the
  // V9.2 surface cards have no such dependency.)
  const [pendingChaseResolve, setPendingChaseResolve] = useState<ChaseSequenceCardProps | null>(null);
  const [pendingPersuasion, setPendingPersuasion] = useState<PersuasionOutcomeChipProps | null>(null);
  const [pendingSeduction, setPendingSeduction] = useState<SeductionStageTrackerProps | null>(null);
  const [pendingFameRecognition, setPendingFameRecognition] = useState<FameRecognitionPanelProps | null>(null);
  const [pendingHeroPointSpend, setPendingHeroPointSpend] = useState<HeroPointSpendViewProps | null>(null);
  const [pendingNpcEncounter, setPendingNpcEncounter] = useState<NPCEncounterCardProps | null>(null);
  const [pendingTravelTime, setPendingTravelTime] = useState<TravelTimeBubbleProps | null>(null);
  const [pendingXpAward, setPendingXpAward] = useState<XPAwardSummaryProps | null>(null);
  const [pendingGambling, setPendingGambling] = useState<GamblingTableViewProps | null>(null);
  const [pendingRepairAttempt, setPendingRepairAttempt] = useState<RepairAttemptIndicatorProps | null>(null);
  const [pendingChargen, setPendingChargen] = useState<CharacterCreationFlowStepperProps | null>(null);

  const dispatcher = useGMDispatcher({
    currentView: currentDispatchView,
    pregenName: pregen.name,
    pregenSkills: pregenSkillsScalar,
    history: dispatchHistory,
    ttsQueue,
    scenarioId: activeScenarioId,
    stateHistory: state.history,
    stateRolls: v5StateRolls,
    inventory: [],
    onPickDecision: onPickDecisionByLabel,
    onRollSkill: onRollSkillFromTool,
    // A8r C4 (2026-06-04): voice-mode GM fame-recognition override.
    // silent:true matches the design doc §6.1 decision — the GM's spoken
    // word IS the reveal, so no per-NPC cinematic queues. The setOverride
    // call is fire-and-forget; we return true synchronously to the
    // dispatcher so its action is treated as dispatched. The Promise
    // rejection is swallowed by setFameOverride's own R2.C1 revert path
    // (UI state reverts; voice log shows nothing extra because that's
    // the same noise as a misclicked manual override).
    onFameOverride: (npcId, recognized) => {
      const setter = setFameOverrideRef.current;
      if (setter === null) return false;
      void setter(npcId, recognized, { silent: true });
      return true;
    },
    // Round 13 (2026-06-06): voice-mode generic dice roll. Routes the
    // GM's parsed (mode, notation) straight into the same pendingGenericRoll
    // state the Other Rolls panel buttons use; the second DiceRollTumble
    // instance mounts and surfaces the result via its chip.
    onForceDiceRoll: (spec) => {
      // Use the instanceId-stamping wrapper so dice-box gets a fresh mount
      // per roll (Sprint 4 §4.5 fix for the dice-accumulation bug).
      setPendingGenericRollWithId(spec);
      return true;
    },
    // Round 15 (2026-06-09): voice-mode paragraph jump. Routes to the
    // SAME goToParagraph callback the History drawer's force-paragraph
    // input + Decision branches use. goToParagraph's findParagraph(n)
    // silently rejects unknown paragraphs (no view change, no history
    // append) — mirroring the History drawer's silent-no-op for
    // out-of-bounds input. Return true unconditionally: out-of-bounds
    // is "dispatched but no observable effect," consistent UX with
    // the click-driven force-paragraph button.
    onForceParagraph: (n) => {
      goToParagraph(n);
      return true;
    },
    // V9.2.x.2 (2026-06-06) — 11 V9.1 dispatch tool callbacks. Each sets
    // the matching pending state slot. The components themselves are
    // pure-presentation; resolved engine output (outcome cells, payouts,
    // hours) is plumbed later via V9.2.0 REST endpoint dispatch.
    onChaseResolve: (args) => {
      setPendingChaseResolve({
        maneuver: args.maneuver,
        bidEaseFactor: args.bidEaseFactor,
        currentRange: args.currentRange,
        rationale: args.rationale,
      });
      return true;
    },
    onPersuasionAttempt: (args) => {
      setPendingPersuasion({
        targetNpcId: args.targetNpcId,
        requestSummary: args.requestSummary,
        efModifierIds: args.efModifierIds,
        rationale: args.rationale,
      });
      return true;
    },
    onSeductionStageAdvance: (args) => {
      setPendingSeduction({
        targetNpcId: args.targetNpcId,
        currentStage: args.currentStage,
        appearanceModifierId: args.appearanceModifierId,
        situationalModifierIds: args.situationalModifierIds,
        rationale: args.rationale,
      });
      return true;
    },
    onFameRecognition: (args) => {
      setPendingFameRecognition({
        targetNpcId: args.targetNpcId,
        disguiseQr: args.disguiseQr,
        rationale: args.rationale,
      });
      return true;
    },
    onHeroPointSpend: (args) => {
      setPendingHeroPointSpend({
        currentBalance: args.currentBalance,
        pointsSpent: args.pointsSpent,
        purpose: args.purpose,
        qrTarget: args.qrTarget,
        rationale: args.rationale,
      });
      return true;
    },
    onNpcEncounterRoll: (args) => {
      setPendingNpcEncounter({
        areaType: args.areaType,
        modifier: args.modifier,
        rationale: args.rationale,
      });
      return true;
    },
    onTravelTimeCompute: (args) => {
      setPendingTravelTime({
        origin: args.origin,
        destination: args.destination,
        mode: args.mode,
        rationale: args.rationale,
      });
      return true;
    },
    onXpAward: (args) => {
      setPendingXpAward({
        rank: args.rank,
        missionOutcome: args.missionOutcome,
        rolePlayMultiplier: args.rolePlayMultiplier,
        rationale: args.rationale,
      });
      return true;
    },
    onGamblingPlay: (args) => {
      setPendingGambling({
        gameType: args.gameType,
        betAmount: args.betAmount,
        rouletteBetType: args.rouletteBetType,
        attemptingCheat: args.attemptingCheat,
        rationale: args.rationale,
      });
      return true;
    },
    onRepairAttempt: (args) => {
      setPendingRepairAttempt({
        d6DamageRoll: args.d6DamageRoll,
        equipmentType: args.equipmentType,
        hasRepairSkill: args.hasRepairSkill,
        rationale: args.rationale,
      });
      return true;
    },
    onCharacterCreationValidate: (args) => {
      setPendingChargen({
        rank: args.rank,
        gender: args.gender,
        heightInches: args.heightInches,
        weightLbs: args.weightLbs,
        appearance: args.appearance,
        characteristics: args.characteristics,
        rationale: args.rationale,
      });
      return true;
    },
    onVoiceLogAppend: voiceLog.append,
    onUndoView,
  });
  dispatcherRef.current = dispatcher;

  // V4 Voice GM (2026-06-02): auto-narrate the current view. Disabled
  // on the ending screen so the player isn't read the closing sting
  // (which has its own ambient sound).
  const autoNarrateView = useMemo(
    () => ({
      key:
        currentDispatchView.kind === 'briefing'
          ? 'briefing'
          : currentDispatchView.kind === 'paragraph'
            ? `p:${currentDispatchView.paragraph_number ?? 'unknown'}`
            : 'ending',
      text: currentDispatchView.paragraph_text ?? '',
      briefText:
        currentDispatchView.kind === 'briefing'
          ? 'Pre-mission briefing.'
          : currentDispatchView.kind === 'paragraph' &&
              typeof currentDispatchView.paragraph_number === 'number'
            ? `Paragraph ${currentDispatchView.paragraph_number}.`
            : 'Mission complete.',
    }),
    [currentDispatchView],
  );
  const visitedParagraphs = useVisitedParagraphs(campaign.id);
  useAutoNarrate(
    autoNarrateView,
    ttsQueue,
    state.view.kind !== 'ending',
    visitedParagraphs,
  );

  // V7.1 NPC character sheets (2026-06-02): resolve any inline NPC ids
  // declared on the current view's data. Briefing uses
  // character_briefing.briefing_npc_inline_refs (closes the "her
  // characteristics are as follows:" papercut); paragraphs use the
  // per-paragraph npc_inline_refs field.
  const npcLookup = useNpcLookup(scenario);
  // A8r (2026-06-04): NPC encounter registry. Tracks which NPCs Bond has
  // met in this campaign + active scenario; auto-records on paragraph
  // navigation by matching the scenario's `encounter_paragraph_first_appearance`
  // field. The encountered set drives the dossier export's NPC list.
  const npcEncounterMap = useMemo(() => {
    // The map is built FRESH each time `scenario` changes (useMemo dep
    // includes scenario). In-place push on locally-allocated arrays is
    // safe here because: (a) we never reference an external array, and
    // (b) the consumer receives the map typed as
    // `ReadonlyMap<number, readonly string[]>` so it cannot mutate it.
    const map = new Map<number, string[]>();
    for (const npc of scenario.key_npcs ?? []) {
      const p = npc.encounter_paragraph_first_appearance;
      if (typeof p !== 'number') continue;
      const existing = map.get(p);
      if (existing) {
        existing.push(npc.id);
      } else {
        map.set(p, [npc.id]);
      }
    }
    return map;
  }, [scenario]);
  const npcEncounterScenarioId = scenario.source_manifest ?? 'unknown';
  const currentParagraphNumber =
    state.view.kind === 'paragraph' ? state.view.number : null;
  const { encounteredIds: encounteredNpcIds, error: npcEncounterError } =
    useNpcEncounters({
      campaignId: campaign.id,
      scenarioId: npcEncounterScenarioId,
      currentParagraph: currentParagraphNumber,
      encounterMap: npcEncounterMap,
    });
  useEffect(() => {
    if (npcEncounterError !== null) {
      console.warn('[A8r] NPC encounter sync failed:', npcEncounterError);
    }
  }, [npcEncounterError]);

  // A8r Slice B (2026-06-04): Fame Recognition. For each newly-encountered
  // TAROT NPC, fire a recognition roll against Bond's fame_points. Successful
  // rolls populate recognizedTarotIds; failed rolls leave the NPC redacted
  // in the dossier (DossierExporter renders ████████ for isTarot && !recognized).
  //
  // Reviewer R1.C3 fix: isTarotFn + npcFameFn callbacks instead of passing
  // npcLookup; keeps the hook's API surface small + symmetric.
  // Reviewer R1.C2 fix: npcFameFn coerces string|number to number (NpcSheetData.
  // combat is Record<string, string | number>); returns 0 fallback when missing.
  const bondFamePoints = pregen.combat.fame_points;
  const isTarotFn = useCallback(
    (npcId: string) => {
      const npc = npcLookup.lookup(npcId);
      return npc?.allegiance === 'tarot';
    },
    [npcLookup],
  );
  const npcFameFn = useCallback(
    (npcId: string) => {
      const npc = npcLookup.lookup(npcId);
      const v = npc?.combat?.fame_points;
      return typeof v === 'number' ? v : 0;
    },
    [npcLookup],
  );
  // A8r Slice C (2026-06-04) — pendingFameReveal state slot mirrors the
  // existing pendingRoll pattern at ScenarioRunner.tsx:986. Set by the
  // onRollFired callback (fresh rolls only — registry-load is silent
  // per the hook's freshlyRolledRef discipline). Cleared by the
  // FameRecognitionCinematic's onComplete.
  const [pendingFameReveal, setPendingFameReveal] = useState<
    { npcId: string; recognized: boolean; npcName: string } | null
  >(null);
  const handleFameRollFired = useCallback(
    (npcId: string, recognized: boolean) => {
      const npc = npcLookup.lookup(npcId);
      const npcName = npc?.name ?? npcId;
      setPendingFameReveal({ npcId, recognized, npcName });
    },
    [npcLookup],
  );

  const {
    recognizedIds: recognizedTarotIds,
    manuallyOverriddenIds: manuallyOverriddenTarotIds,  // A8r Slice D
    crossScenarioRecognizedIds: crossScenarioRecognizedTarotIds,  // A8r Slice D
    crossScenarioRecognitions: crossScenarioRecognitionsByNpc,  // A8r C2
    error: fameRecognitionError,
    setOverride: setFameOverride,  // A8r Slice C/D — 3-state override
  } = useFameRecognition({
      campaignId: campaign.id,
      scenarioId: npcEncounterScenarioId,
      bondFamePoints,
      encounteredIds: encounteredNpcIds,
      isTarotFn,
      npcFameFn,
      onRollFired: handleFameRollFired,  // A8r Slice C — cinematic trigger
      onOverrideFired: handleFameRollFired,  // A8r Slice C5 — same trigger on GM force-recognize
    });
  // A8r C4 (2026-06-04): publish setFameOverride through the forward-ref so
  // the dispatcher (declared above this destructure) can resolve it at
  // call-time. See the comment on setFameOverrideRef's declaration.
  setFameOverrideRef.current = setFameOverride;
  useEffect(() => {
    if (fameRecognitionError !== null) {
      console.warn(
        '[A8r/Slice B] Fame recognition sync failed:',
        fameRecognitionError,
      );
    }
  }, [fameRecognitionError]);

  const dossierNpcs = useMemo<DossierNpc[]>(
    () =>
      npcLookup.all
        .filter((n) => encounteredNpcIds.has(n.id))
        .map((n) => ({
          id: n.id,
          name: n.name,
          // A8r Slice B (2026-06-04): isTarot now WORKS because
          // personnelCatalog tags tarot.json dossiers with allegiance='tarot'
          // (defaultAllegiance flipped from 'enemy' to 'tarot' in commit 1).
          // Scenario-supplied key_npcs can also set allegiance='tarot'
          // explicitly to opt into the TAROT-redaction path.
          isTarot: n.allegiance === 'tarot',
          description: n.sketch,
        })),
    [npcLookup.all, encounteredNpcIds],
  );

  // Compose final dossier recognition set:
  //   - Non-TAROT encountered NPCs: always recognized (no roll fires)
  //   - TAROT encountered NPCs: recognized in THIS scenario OR carries
  //     across from a prior scenario (A8r Slice D cross-scenario fame)
  //
  // Reviewer R1.C6 fix: isTarotFn is in the deps array intentionally so the
  // memo recomputes when scenario changes (npcLookup reference change →
  // isTarotFn callback re-created). Do NOT remove from deps even though it
  // "looks unchanging at runtime" — removing breaks the allegiance check for
  // NPCs in scenarios that haven't loaded their key_npcs yet.
  const recognizedIdsForDossier = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    for (const id of encounteredNpcIds) {
      if (isTarotFn(id)) {
        // A8r Slice D: cross-scenario carry preserves recognition narrative
        // even when this scenario has no natural roll yet.
        if (recognizedTarotIds.has(id) || crossScenarioRecognizedTarotIds.has(id)) {
          out.add(id);
        }
      } else {
        out.add(id); // non-TAROT always recognized
      }
    }
    return out;
  }, [encounteredNpcIds, recognizedTarotIds, crossScenarioRecognizedTarotIds, isTarotFn]);

  const briefingInlineNpcs = useMemo(
    () => npcLookup.lookupMany(scenario.character_briefing.briefing_npc_inline_refs ?? []),
    [npcLookup],
  );
  const paragraphInlineNpcs = useMemo(
    () => npcLookup.lookupMany(currentParagraph?.npc_inline_refs ?? []),
    [npcLookup, currentParagraph],
  );

  // V7.2.A (2026-06-02): scan the current paragraph's text for an inline
  // roll trigger ("Roll against your Evasion at EF 5"). When detected,
  // the QuickRollButton renders below the prose.
  // 2026-06-08 Fix #B — also scan decision labels. Try body first (so
  // body-stated triggers keep their canonical position); fall back to a
  // body+labels scan when the body alone yielded null. This is how the
  // AVTAK ¶6 Perception / ¶33 Mountaineering / ¶38 Demolitions / ¶46
  // Mountaineering rolls (specified only in decision labels) start
  // surfacing a pre-filled QuickRollButton.
  const paragraphRollTrigger = useMemo(() => {
    if (currentParagraph === null || currentParagraph === undefined) return null;
    const bodyHit = detectRollTrigger(currentParagraph.text);
    if (bodyHit !== null) return bodyHit;
    if (!currentParagraph.decisions || currentParagraph.decisions.length === 0)
      return null;
    const combined = currentParagraph.decisions.map((d) => d.label).join(' ');
    return detectRollTrigger(combined);
  }, [currentParagraph],
  );
  const paragraphRollAvailable = useMemo(
    () =>
      paragraphRollTrigger !== null &&
      // 2026-06-04 dice-roll-feedback fix: use resolveSkillToPregenKey
      // so the QuickRollButton enables when the trigger skill maps to
      // a pregen skill via the SKILL_ALIASES alias map (e.g.
      // "Perception" → "Sixth Sense"). Direct .some() lookup missed
      // every aliased trigger and silently disabled the button.
      resolveSkillToPregenKey(
        paragraphRollTrigger.skill,
        Object.keys(pregen.skills),
      ) !== null,
    [paragraphRollTrigger, pregen.skills],
  );
  // 2026-06-09 Bug 1 fix: threaded to ParagraphView → DecisionList so
  // skill_check edges disable when the PC lacks the required skill. The
  // key list (not the skill values) is all DecisionList needs — it only
  // calls resolveSkillToPregenKey to map the edge's `skillName` against
  // the available pregen keys.
  const pregenSkillKeys = useMemo(
    () => Object.keys(pregen.skills),
    [pregen.skills],
  );

  return (
    <TtsQueueProvider value={ttsQueue}>
    <div className="br-scenario-runner" data-testid="br-scenario-runner">
      {pendingRoll && (
        <DiceRollTumble
          onComplete={handleTumbleComplete}
          mode={cinematicMode === 'voice' ? 'voice' : 'selector'}
          voiceIntent={cinematicVoiceIntent}
          rollMode="skill_check"
          // 2026-06-07: surface the derivation breakdown on the cinematic
          // chip itself so the player sees PC × EF → SC + verdict + QR
          // without opening the Roll Dice drawer. Per
          // feedback_dice_roll_outcome_not_interpreted memory anchor.
          interpretation={{
            skillName: pendingRoll.skill_name ?? '(unknown skill)',
            easeFactor: pendingRoll.ease_factor,
            primaryChance: pendingRoll.primary_chance,
            successChance: pendingRoll.success_chance,
            qualityRating: pendingRoll.quality_rating,
            qualityName: pendingRoll.quality_name,
          }}
          // 2026-06-08 F5e fix — patch pendingRoll + last state.rolls entry
          // from the dice-box's actual outcome so persisted state matches
          // what the player saw on screen. See handleVerdictResolved doc
          // comment for the full double-RNG → single-RNG migration story.
          onVerdictResolved={handleVerdictResolved}
          // 2026-06-08 Workstream B: thread HP props so the chip renders the
          // Spend [H] button inline. handleSpendHeroPoint already guards
          // quality_rating===1 + !canSpendHeroPoint; SkillCheckInterpretation
          // disables the button at those same conditions as a belt-and-braces
          // UX affordance. QR-upgrade math NOT duplicated — click delegates to
          // the existing spendHeroPoint → setChipFlying → handleChipAnimationComplete chain.
          onSpendHeroPoint={handleSpendHeroPoint}
          heroPoints={heroPoints}
          canSpendHeroPoint={canSpendHeroPoint}
        />
      )}
      {pendingGenericRoll && (
        <DiceRollTumble
          // Sprint 4 §4.5 (2026-06-06): key forces full unmount+remount per
          // roll → fresh dice-box scene. Closes the user-reported "dice
          // accumulating across rolls" bug on 1.300.0 (see instanceId comment
          // on the pendingGenericRoll state above).
          key={pendingGenericRoll.instanceId}
          onComplete={() => setPendingGenericRoll(null)}
          mode="selector"
          rollMode={pendingGenericRoll.mode}
          notation={pendingGenericRoll.notation}
          testidPrefix="br-dice-roll-tumble-generic"
          // Round 14 (Sprint 2 §4.4): append to genericRolls when dice settle.
          // Also stash on the bridge ref so onChipReady can compose the
          // portal payload without racing the React batch.
          onResult={(r) => {
            lastGenericResultRef.current = r;
            setGenericRolls((prev) => [
              ...prev,
              {
                mode: r.mode,
                notation: pendingGenericRoll.notation,
                total: r.total,
                breakdown: r.breakdown,
                timestamp: Date.now(),
              },
            ]);
          }}
          // Round 16 (2026-06-09): chip-fly NARROWED to skill_check only.
          // Pre-Round-16 the fly fired for save/damage/skill_check
          // (percentile already opted out per Round 14 Sprint 2 §4.3 — QR
          // cinematic IS the reveal there). User audit caught the
          // redundancy: the cinematic IS the reveal for save/damage too,
          // and Round 16's new DamageInterpretation block makes damage
          // chips information-dense enough that the fly adds no value.
          // Only skill_check chips carry rich derivation (PC × EF → SC +
          // verdict + QR-tier explainer) worth animating into history.
          //
          // Atmosphere setting `diceChipFlyEnabled` is preserved
          // (persisted-state compat) — its effective scope is now
          // "skill_check chip-fly toggle." AtmosphereSettingsPanel
          // label + description updated this round to reflect the new
          // scope honestly.
          onChipReady={(el) => {
            if (pendingGenericRoll.mode !== 'skill_check') return;
            const r = lastGenericResultRef.current;
            if (!r) return;
            const rect = el.getBoundingClientRect();
            if (rect.width <= 0 && rect.height <= 0) return;
            setPendingChipFly({
              sourcePoint: {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
              },
              result: r,
              notation: pendingGenericRoll.notation,
            });
          }}
        />
      )}
      {pendingChipFly && (
        <DiceChipPortal
          sourcePoint={pendingChipFly.sourcePoint}
          targetRef={historyToggleRef}
          result={pendingChipFly.result}
          notation={pendingChipFly.notation}
          onAnimationComplete={() => setPendingChipFly(null)}
        />
      )}
      {/* A8r Slice C (2026-06-04) — Fame Recognition cinematic mount. The
          cinematic only fires for FRESH rolls (per useFameRecognition's
          freshlyRolledRef discipline — registry-load on SPA reload is
          silent). The atmosphere flag is checked INSIDE the cinematic
          component; when off, the component renders nothing and fires
          onComplete immediately so the state slot clears. */}
      {pendingFameReveal && (
        <FameRecognitionCinematic
          npcName={pendingFameReveal.npcName}
          recognized={pendingFameReveal.recognized}
          onComplete={() => setPendingFameReveal(null)}
        />
      )}
      {/* V9.2.x.2 (2026-06-06) — V9.1 dispatch surface stack. Each card
          renders when its useGMDispatcher callback sets its state slot.
          Stack persists across paragraph navigation; later same-tool
          dispatches REPLACE the previous card. The "Dismiss all" pill
          clears the entire stack — per-card dismiss UI is V9.2.x.3. */}
      {(pendingChaseResolve ||
        pendingPersuasion ||
        pendingSeduction ||
        pendingFameRecognition ||
        pendingHeroPointSpend ||
        pendingNpcEncounter ||
        pendingTravelTime ||
        pendingXpAward ||
        pendingGambling ||
        pendingRepairAttempt ||
        pendingChargen) && (
        <div
          className="br-v92-dispatch-stack"
          data-testid="br-v92-dispatch-stack"
        >
          <button
            type="button"
            className="br-v92-dispatch-stack-dismiss"
            data-testid="br-v92-dispatch-stack-dismiss"
            onClick={() => {
              setPendingChaseResolve(null);
              setPendingPersuasion(null);
              setPendingSeduction(null);
              setPendingFameRecognition(null);
              setPendingHeroPointSpend(null);
              setPendingNpcEncounter(null);
              setPendingTravelTime(null);
              setPendingXpAward(null);
              setPendingGambling(null);
              setPendingRepairAttempt(null);
              setPendingChargen(null);
            }}
          >
            Dismiss all
          </button>
          {pendingChaseResolve && (
            <ChaseSequenceCard
              {...pendingChaseResolve}
              onDismiss={() => setPendingChaseResolve(null)}
            />
          )}
          {pendingPersuasion && (
            <PersuasionOutcomeChip
              {...pendingPersuasion}
              onDismiss={() => setPendingPersuasion(null)}
            />
          )}
          {pendingSeduction && (
            <SeductionStageTracker
              {...pendingSeduction}
              onDismiss={() => setPendingSeduction(null)}
            />
          )}
          {pendingFameRecognition && (
            <FameRecognitionPanel
              {...pendingFameRecognition}
              onDismiss={() => setPendingFameRecognition(null)}
            />
          )}
          {pendingHeroPointSpend && (
            <HeroPointSpendView
              {...pendingHeroPointSpend}
              onDismiss={() => setPendingHeroPointSpend(null)}
            />
          )}
          {pendingNpcEncounter && (
            <NPCEncounterCard
              {...pendingNpcEncounter}
              onDismiss={() => setPendingNpcEncounter(null)}
            />
          )}
          {pendingTravelTime && (
            <TravelTimeBubble
              {...pendingTravelTime}
              onDismiss={() => setPendingTravelTime(null)}
            />
          )}
          {pendingXpAward && (
            <XPAwardSummary
              {...pendingXpAward}
              onDismiss={() => setPendingXpAward(null)}
            />
          )}
          {pendingGambling && (
            <GamblingTableView
              {...pendingGambling}
              onDismiss={() => setPendingGambling(null)}
            />
          )}
          {pendingRepairAttempt && (
            <RepairAttemptIndicator
              {...pendingRepairAttempt}
              onDismiss={() => setPendingRepairAttempt(null)}
            />
          )}
          {pendingChargen && (
            <CharacterCreationFlowStepper
              {...pendingChargen}
              onDismiss={() => setPendingChargen(null)}
            />
          )}
        </div>
      )}
      {qrPending && (
        <QRCinematicResponse
          key={cinematicMountId}
          roll={qrPending}
          onComplete={handleQRComplete}
          mode={cinematicMode === 'voice' ? 'voice' : 'selector'}
        />
      )}
      {chipFlying && (
        <HeroPointChipPortal
          sourceRef={heroPointSourceRef}
          targetRef={rollResultTargetRef}
          onAnimationComplete={handleChipAnimationComplete}
        />
      )}
      <header
        className="br-scenario-runner-header"
        data-testid="br-scenario-runner-header"
      >
        {onBackToBriefingRoom && (
          <button
            type="button"
            className="br-back-to-briefing-room"
            data-testid="br-back-to-briefing-room"
            onClick={onBackToBriefingRoom}
          >
            ← Briefing Room
          </button>
        )}
        <div className="br-scenario-runner-meta">
          {/* 2026-06-04 British-flavor pass — Union Jack + Crown badge
              precede the campaign meta to anchor the "H.M. Government
              dossier" voice without dominating the line. */}
          <BritishInsignia />
          <p className="br-label">
            {campaign.name} · {pregen.name} (Rank {pregen.rank}) · Fame{' '}
            {pregen.combat.fame_points}
            <HelpIcon id="fame-points" side="below" />
            {' · '}
            <span ref={heroPointSourceRef} data-testid="br-scenario-hero-points">
              Hero {heroPoints}
            </span>
            <HelpIcon id="hero-points" side="below" />
          </p>
        </div>
        <nav className="br-scenario-drawer-toggles">
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-stats-toggle"
              onClick={() => toggleDrawer('stats')}
              aria-pressed={drawer === 'stats'}
            >
              Stats
            </button>
            <HelpIcon id="stats-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-history-toggle"
              onClick={() => toggleDrawer('history')}
              aria-pressed={drawer === 'history'}
              ref={historyToggleRef}
            >
              History
            </button>
            <HelpIcon id="history-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-roll-toggle"
              onClick={() => toggleDrawer('roll')}
              aria-pressed={drawer === 'roll'}
            >
              Roll Dice
            </button>
            <HelpIcon id="roll-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-audio-toggle"
              onClick={() => toggleDrawer('audio')}
              aria-pressed={drawer === 'audio'}
            >
              Audio
            </button>
            <HelpIcon id="audio-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-atmosphere-toggle"
              onClick={() => toggleDrawer('atmosphere')}
              aria-pressed={drawer === 'atmosphere'}
            >
              Atmosphere
            </button>
            <HelpIcon id="atmosphere-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-voice-log-toggle"
              onClick={() => toggleDrawer('voice-log')}
              aria-pressed={drawer === 'voice-log'}
            >
              Voice Log
            </button>
            <HelpIcon id="voice-log-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-npcs-toggle"
              onClick={() => toggleDrawer('npcs')}
              aria-pressed={drawer === 'npcs'}
            >
              NPCs
            </button>
            <HelpIcon id="npcs-drawer" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-pdf-library-toggle"
              onClick={() => toggleDrawer('pdf-library')}
              aria-pressed={drawer === 'pdf-library'}
            >
              PDF Library
            </button>
            <HelpIcon id="pdf-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-modules-library-toggle"
              onClick={() => toggleDrawer('modules-library')}
              aria-pressed={drawer === 'modules-library'}
            >
              Modules
            </button>
            <HelpIcon id="modules-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-equipment-library-toggle"
              onClick={() => toggleDrawer('equipment-library')}
              aria-pressed={drawer === 'equipment-library'}
            >
              Equipment
            </button>
            <HelpIcon id="equipment-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-vehicles-library-toggle"
              onClick={() => toggleDrawer('vehicles-library')}
              aria-pressed={drawer === 'vehicles-library'}
            >
              Vehicles
            </button>
            <HelpIcon id="vehicles-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-gadgets-library-toggle"
              onClick={() => toggleDrawer('gadgets-library')}
              aria-pressed={drawer === 'gadgets-library'}
            >
              Gadgets
            </button>
            <HelpIcon id="gadgets-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-drugs-library-toggle"
              onClick={() => toggleDrawer('drugs-library')}
              aria-pressed={drawer === 'drugs-library'}
            >
              Drugs
            </button>
            <HelpIcon id="drugs-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-q-branch-personnel-library-toggle"
              onClick={() => toggleDrawer('q-branch-personnel-library')}
              aria-pressed={drawer === 'q-branch-personnel-library'}
            >
              Q Branch
            </button>
            <HelpIcon id="q-branch-personnel-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-npc-archetypes-library-toggle"
              onClick={() => toggleDrawer('npc-archetypes-library')}
              aria-pressed={drawer === 'npc-archetypes-library'}
            >
              NPC Archetypes
            </button>
            <HelpIcon id="npc-archetypes-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-canonical-npcs-library-toggle"
              onClick={() => toggleDrawer('canonical-npcs-library')}
              aria-pressed={drawer === 'canonical-npcs-library'}
            >
              Canonical NPCs
            </button>
            <HelpIcon id="canonical-npcs-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-villains-dossier-library-toggle"
              onClick={() => toggleDrawer('villains-dossier-library')}
              aria-pressed={drawer === 'villains-dossier-library'}
            >
              Villains
            </button>
            <HelpIcon id="villains-dossier-library" side="below" />
          </span>
          <span className="br-scenario-drawer-toggle-group">
            <button
              type="button"
              data-testid="br-scenario-drawer-thrilling-locations-npcs-library-toggle"
              onClick={() => toggleDrawer('thrilling-locations-npcs-library')}
              aria-pressed={drawer === 'thrilling-locations-npcs-library'}
            >
              Venue Staff
            </button>
            <HelpIcon id="thrilling-locations-npcs-library" side="below" />
          </span>
          {themeSelection !== undefined && (
            <span className="br-scenario-drawer-toggle-group">
              <button
                type="button"
                data-testid="br-scenario-drawer-theme-toggle"
                onClick={() => toggleDrawer('theme')}
                aria-pressed={drawer === 'theme'}
              >
                Theme
              </button>
              <HelpIcon id="theme-drawer" side="below" />
            </span>
          )}
        </nav>
        <SettingsToggles />
      </header>

      <TalkBar onTranscript={handleTranscript} />

      {/* Sprint 4 §4.5 (2026-06-06): voice-transcript debug control. Mounts
       *  when atmosphere voiceDebugEnabled is ON; fixed bottom-right overlay
       *  with a textarea + Send button that calls the same handleTranscript
       *  the mic does. Used by briefing-room-voice-force-dice-roll Playwright
       *  spec for mocked-route tests (mic permission is brittle in headless). */}
      <VoiceDebugControl onTranscript={handleTranscript} />

      <main className="br-scenario-runner-main">
        {state.view.kind === 'briefing' && (
          <BriefingView
            onChoose={goToParagraph}
            onTranscript={handleTranscript}
            inlineNpcs={briefingInlineNpcs}
          />
        )}
        {state.view.kind === 'paragraph' && currentParagraph && (
          <ParagraphView
            paragraph={currentParagraph}
            onChoose={goToParagraph}
            onTranscript={handleTranscript}
            inlineNpcs={paragraphInlineNpcs}
            rollTrigger={paragraphRollTrigger}
            rollSkillAvailable={paragraphRollAvailable}
            onQuickRoll={handleQuickRoll}
            // 2026-06-07: opens the Roll drawer so the player can pick
            // skill + EF manually.
            onPickRoll={() => toggleDrawer('roll')}
            // Workstream A 2026-06-08: skill_check edge routing.
            onSkillCheckEdge={handleSkillCheckEdge}
            // 2026-06-09 Bug 1 fix: disable skill_check edges whose
            // skill name doesn't resolve in the PC's pregen.
            pregenSkillKeys={pregenSkillKeys}
          />
        )}
        {state.view.kind === 'paragraph' && !currentParagraph && (
          <article
            className="br-scenario-paragraph"
            data-testid="br-scenario-paragraph-missing"
          >
            <p className="br-body">
              Data integrity: ¶{state.view.number} is not in the published
              1-102 range. Use the drawer toggles or Play Again to recover.
            </p>
          </article>
        )}
        {state.view.kind === 'ending' && (
          <EndingView
            paragraphNumber={state.view.paragraph}
            endingType={state.view.ending_type}
            history={state.history}
            onPlayAgain={handlePlayAgain}
          />
        )}
      </main>

      {/* D-track polish (2026-06-02): mobile drawer backdrop. Renders only
          when a drawer panel is open. Display: none on viewports ≥ 900px
          (where the drawer side-pins via :has() grid per D-6). On mobile
          (< 900px) this is a full-viewport tap-to-dismiss overlay behind
          the bottom-sheet drawer. Button element (not div) for native
          keyboard + screen-reader semantics. */}
      {drawer !== null && (
        <button
          type="button"
          className="br-scenario-drawer-backdrop"
          data-testid="br-scenario-drawer-backdrop"
          aria-label="Close drawer"
          onClick={() => setDrawer(null)}
        />
      )}
      {drawer === 'stats' && <StatsPanel pregen={pregen} />}
      {drawer === 'history' && (
        <HistoryPanel
          history={state.history}
          rolls={state.rolls}
          missionName={campaign.name}
          dossierNpcs={dossierNpcs}
          // A8r Slice B (2026-06-04): non-TAROT auto ∪ TAROT-rolled-passed
          // ∪ cross-scenario carry (Slice D).
          recognizedNpcIds={recognizedIdsForDossier}
          // A8r Slice D (2026-06-04): manual-badge + cross-scenario surface
          // through to HistoryPanel for the 3-state segmented toggle + the
          // Fame Recognition narrative section.
          manuallyOverriddenNpcIds={manuallyOverriddenTarotIds}
          crossScenarioRecognizedNpcIds={crossScenarioRecognizedTarotIds}
          crossScenarioRecognitionsByNpc={crossScenarioRecognitionsByNpc}
          // A8r Slice C/D: 3-state setOverride (boolean | null).
          setFameOverride={setFameOverride}
          // Round 14 (Sprint 2 §4.4): generic Save/Damage/Skill Check/
          // Percentile rolls.
          genericRolls={genericRolls}
          // 2026-06-07: GM force-paragraph escape hatch.
          onForceParagraph={goToParagraph}
          maxParagraph={scenario.story_paragraphs.length}
        />
      )}
      {drawer === 'roll' && (
        <RollDicePanel
          pregen={pregen}
          rollSkill={rollSkill}
          rollEF={rollEF}
          onSkillChange={setRollSkill}
          onEFChange={setRollEF}
          onRoll={handleRollDice}
          lastRoll={lastRoll}
          resultRef={rollResultTargetRef}
          heroPoints={heroPoints}
          canSpendHeroPoint={canSpendHeroPoint}
          onSpendHeroPoint={handleSpendHeroPoint}
          onGenericRoll={(spec) => setPendingGenericRollWithId(spec)}
        />
      )}
      {drawer === 'audio' && (
        <AudioPanel
          muted={audioMuted}
          volume={audioVolume}
          currentCueId={currentCueId}
          unavailable={audioUnavailable}
          onToggleMute={handleToggleMute}
          onVolumeChange={handleVolumeChange}
        />
      )}
      {drawer === 'atmosphere' && (
        // 2026-06-05 Bug Hunt Finding #4: wrap with the canonical
        // `br-scenario-drawer-{name}` testid so the atmosphere drawer
        // follows the same naming convention as the other 7. The panel
        // keeps its existing `br-atmosphere-settings-panel` testid for
        // backwards compatibility with AtmosphereSettingsPanel tests.
        <div data-testid="br-scenario-drawer-atmosphere">
          <AtmosphereSettingsPanel />
        </div>
      )}
      {drawer === 'voice-log' && (
        <VoiceLogDrawer entries={voiceLog.entries} onClear={voiceLog.clear} />
      )}
      {drawer === 'npcs' && (
        <NpcDrawer
          npcs={npcLookup.all}
          recognizedIds={recognizedIdsForDossier}
        />
      )}
      {drawer === 'pdf-library' && (
        <PdfLibraryDrawer
          pdfs={pdfLibrary.pdfs}
          state={pdfLibrary.state}
          error={pdfLibrary.error}
          onSelect={(path) => {
            const meta = pdfLibrary.pdfs.find((p) => p.path === path);
            setPdfModal({ path, title: meta?.title ?? path });
          }}
        />
      )}
      {drawer === 'modules-library' && (
        <ModulesLibraryDrawer
          modules={modulesLibrary.modules}
          state={modulesLibrary.state}
          error={modulesLibrary.error}
          selectedModuleId={modulesLibrary.selectedModuleId}
          selectedModuleData={modulesLibrary.selectedModuleData}
          selectedModuleState={modulesLibrary.selectedModuleState}
          onSelect={(moduleId) =>
            modulesLibrary.selectModule(
              modulesLibrary.selectedModuleId === moduleId ? null : moduleId,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'equipment-library' && (
        <EquipmentLibraryDrawer
          weapons={equipmentLibrary.weapons}
          state={equipmentLibrary.state}
          error={equipmentLibrary.error}
          selectedWeaponId={equipmentLibrary.selectedWeaponId}
          selectedWeaponData={equipmentLibrary.selectedWeaponData}
          selectedWeaponState={equipmentLibrary.selectedWeaponState}
          onSelect={(weaponId) =>
            equipmentLibrary.selectWeapon(
              equipmentLibrary.selectedWeaponId === weaponId ? null : weaponId,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'q-branch-personnel-library' && (
        <QBranchPersonnelDrawer
          personnel={qBranchPersonnelLibrary.personnel}
          state={qBranchPersonnelLibrary.state}
          error={qBranchPersonnelLibrary.error}
          activeTab={qBranchPersonnelLibrary.activeTab}
          onTabChange={qBranchPersonnelLibrary.setActiveTab}
          selectedPersonnelId={qBranchPersonnelLibrary.selectedPersonnelId}
          selectedPersonnelData={qBranchPersonnelLibrary.selectedPersonnelData}
          selectedPersonnelState={qBranchPersonnelLibrary.selectedPersonnelState}
          onSelectPersonnel={(id) =>
            qBranchPersonnelLibrary.selectPersonnel(
              qBranchPersonnelLibrary.selectedPersonnelId === id ? null : id,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'npc-archetypes-library' && (
        <NpcArchetypesDrawer
          archetypes={npcArchetypesLibrary.archetypes}
          state={npcArchetypesLibrary.state}
          error={npcArchetypesLibrary.error}
          activeTab={npcArchetypesLibrary.activeTab}
          onTabChange={npcArchetypesLibrary.setActiveTab}
          selectedArchetypeId={npcArchetypesLibrary.selectedArchetypeId}
          selectedArchetypeData={npcArchetypesLibrary.selectedArchetypeData}
          selectedArchetypeState={npcArchetypesLibrary.selectedArchetypeState}
          onSelectArchetype={(id) =>
            npcArchetypesLibrary.selectArchetype(
              npcArchetypesLibrary.selectedArchetypeId === id ? null : id,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'canonical-npcs-library' && (
        <CanonicalNpcsDrawer
          npcs={canonicalNpcsLibrary.npcs}
          pendingRemainingCount={canonicalNpcsLibrary.pendingRemainingCount}
          state={canonicalNpcsLibrary.state}
          error={canonicalNpcsLibrary.error}
          activeTab={canonicalNpcsLibrary.activeTab}
          onTabChange={canonicalNpcsLibrary.setActiveTab}
          selectedNpcId={canonicalNpcsLibrary.selectedNpcId}
          selectedNpcData={canonicalNpcsLibrary.selectedNpcData}
          selectedNpcState={canonicalNpcsLibrary.selectedNpcState}
          onSelectNpc={(id) =>
            canonicalNpcsLibrary.selectNpc(
              canonicalNpcsLibrary.selectedNpcId === id ? null : id,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'villains-dossier-library' && (
        <VillainsDossierDrawer
          npcs={villainsLibrary.npcs}
          pendingRemainingCount={villainsLibrary.pendingRemainingCount}
          state={villainsLibrary.state}
          error={villainsLibrary.error}
          activeTab={villainsLibrary.activeTab}
          onTabChange={villainsLibrary.setActiveTab}
          selectedNpcId={villainsLibrary.selectedNpcId}
          selectedNpcData={villainsLibrary.selectedNpcData}
          selectedNpcState={villainsLibrary.selectedNpcState}
          onSelectNpc={(id) =>
            villainsLibrary.selectNpc(
              villainsLibrary.selectedNpcId === id ? null : id,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'thrilling-locations-npcs-library' && (
        <ThrillingLocationsNpcsDrawer
          npcs={thrillingLocationsLibrary.npcs}
          pendingRemainingCount={thrillingLocationsLibrary.pendingRemainingCount}
          state={thrillingLocationsLibrary.state}
          error={thrillingLocationsLibrary.error}
          activeTab={thrillingLocationsLibrary.activeTab}
          onTabChange={thrillingLocationsLibrary.setActiveTab}
          selectedNpcId={thrillingLocationsLibrary.selectedNpcId}
          selectedNpcData={thrillingLocationsLibrary.selectedNpcData}
          selectedNpcState={thrillingLocationsLibrary.selectedNpcState}
          onSelectNpc={(id) =>
            thrillingLocationsLibrary.selectNpc(
              thrillingLocationsLibrary.selectedNpcId === id ? null : id,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'drugs-library' && (
        <DrugsLibraryDrawer
          drugs={drugsLibrary.drugs}
          state={drugsLibrary.state}
          error={drugsLibrary.error}
          activeTab={drugsLibrary.activeTab}
          onTabChange={drugsLibrary.setActiveTab}
          selectedDrugId={drugsLibrary.selectedDrugId}
          selectedDrugData={drugsLibrary.selectedDrugData}
          selectedDrugState={drugsLibrary.selectedDrugState}
          onSelectDrug={(drugId) =>
            drugsLibrary.selectDrug(
              drugsLibrary.selectedDrugId === drugId ? null : drugId,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'gadgets-library' && (
        <GadgetsLibraryDrawer
          gadgets={gadgetsLibrary.gadgets}
          state={gadgetsLibrary.state}
          error={gadgetsLibrary.error}
          activeTab={gadgetsLibrary.activeTab}
          onTabChange={gadgetsLibrary.setActiveTab}
          selectedGadgetId={gadgetsLibrary.selectedGadgetId}
          selectedGadgetData={gadgetsLibrary.selectedGadgetData}
          selectedGadgetState={gadgetsLibrary.selectedGadgetState}
          onSelectGadget={(gadgetId) =>
            gadgetsLibrary.selectGadget(
              gadgetsLibrary.selectedGadgetId === gadgetId ? null : gadgetId,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'vehicles-library' && (
        <VehiclesLibraryDrawer
          vehicles={vehiclesLibrary.vehicles}
          vehiclesState={vehiclesLibrary.vehiclesState}
          vehiclesError={vehiclesLibrary.vehiclesError}
          modifications={vehiclesLibrary.modifications}
          modificationsState={vehiclesLibrary.modificationsState}
          modificationsError={vehiclesLibrary.modificationsError}
          activeTab={vehiclesLibrary.activeTab}
          onTabChange={vehiclesLibrary.setActiveTab}
          selectedVehicleId={vehiclesLibrary.selectedVehicleId}
          selectedVehicleData={vehiclesLibrary.selectedVehicleData}
          selectedVehicleState={vehiclesLibrary.selectedVehicleState}
          onSelectVehicle={(vehicleId) =>
            vehiclesLibrary.selectVehicle(
              vehiclesLibrary.selectedVehicleId === vehicleId ? null : vehicleId,
            )
          }
          selectedModificationId={vehiclesLibrary.selectedModificationId}
          selectedModificationData={vehiclesLibrary.selectedModificationData}
          selectedModificationState={vehiclesLibrary.selectedModificationState}
          onSelectModification={(modId) =>
            vehiclesLibrary.selectModification(
              vehiclesLibrary.selectedModificationId === modId ? null : modId,
            )
          }
          onClose={() => setDrawer(null)}
        />
      )}
      {drawer === 'theme' && themeSelection !== undefined && (
        <ThemeDrawer
          theme={themeSelection.theme}
          setTheme={themeSelection.setTheme}
          themes={themeSelection.themes}
        />
      )}
      {pdfModal !== null && (
        <PdfViewerModal
          path={pdfModal.path}
          title={pdfModal.title}
          onClose={() => setPdfModal(null)}
        />
      )}
      <UndoBanner
        snapshot={
          dispatcher.lastSnapshot &&
          dispatcher.lastSnapshot.action &&
          (dispatcher.lastSnapshot.action.tool === 'pick_decision' ||
            dispatcher.lastSnapshot.action.tool === 'roll_skill')
            ? dispatcher.lastSnapshot
            : null
        }
        onUndo={dispatcher.undo}
        onExpire={dispatcher.clearSnapshot}
      />
      <AckBanner
        snapshot={
          dispatcher.lastSnapshot &&
          dispatcher.lastSnapshot.action &&
          dispatcher.lastSnapshot.action.tool !== 'pick_decision' &&
          dispatcher.lastSnapshot.action.tool !== 'roll_skill'
            ? dispatcher.lastSnapshot
            : null
        }
        onExpire={dispatcher.clearSnapshot}
      />
    </div>
    </TtsQueueProvider>
  );
}

export default ScenarioRunner;
